/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-review.js — Review step controller (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `review`. Final step in every flow.
 *
 * Phase 2.5 layout per:
 *   - mockups/multistep-step5-review-single-size-{desktop,mobile}.html
 *   - mockups/multistep-step5-review-multi-size-{desktop,mobile}.html
 *   - mockups/bundle-step5-review-{desktop,mobile}.html
 *
 * The cart-edit variant (Phase 4) is OUT OF SCOPE this session. The
 * .is-cart-edit-mode hook from the previous review rebuild is preserved
 * for Phase 4 to wire.
 *
 * Three rendered variants in this controller, branched at render time:
 *   1. single_size_multistep — state.flow='multistep' && sizes.length<=1
 *   2. multi_size_multistep   — state.flow='multistep' && sizes.length>1
 *   3. bundle                 — state.flow='bundle'
 *
 * Sections (all variants):
 *   - .jrs-review__title + .jrs-review__subtitle (count-aware)
 *   - .jrs-product-card with image, title, meta, price-row, Change button
 *   - Multi-size adds .jrs-size-list nested in the product card
 *   - Bundle adds .jrs-bundle-summary (rendered when context.bundle_children
 *     is populated; falls back to "X-item bundle" line when not)
 *   - One .jrs-logo-group per UNIQUE logo (NOT per position). Header shows
 *     thumbnail + filename + "Used in N positions" label + Change button.
 *     Body is a .jrs-position-list of .jrs-position-row entries (name +
 *     application method label + Edit link). Multiple logo-groups when
 *     logo_strategy='different_logos' produces distinct logos per position.
 *   - .jrs-trust-band with 3 guarantee rows
 *
 * Footer Confirm CTA remains the Path C placeholder — fires
 * pdp_customizer_confirm telemetry + console.warn. Cart-submit wiring is
 * a focused follow-up session.
 *
 * Sticky right-rail summary panel from the desktop mockups is NOT
 * implemented this session. It overlaps with cart-submit pricing math
 * (subtotal × qty + branding free line + total + VAT) which is the
 * cart-submit adapter's territory. Phase 5 polish item: when the
 * cart-payload adapter lands, derive the summary numbers from the same
 * computed payload and conditionally render the right-rail on desktop.
 *
 * Data sources (read at onEnter):
 *   state.draft.positions, .application_methods, .logo_strategy,
 *     .logo_assignments, .text_logo
 *   state.flow ('multistep' | 'bundle')
 *   window.JRS_PDP_CONTEXT.{productTitle, productImage, productColour,
 *     productPricing, sizes, bundle_children}
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'review';

  // Instrumentation (2026-09-03): confirm attempts per popup session so retry
  // loops are visible in a single event without session stitching.
  var _confirmAttempts = {};
  function _nextAttemptIndex() {
    var sid = '';
    try { sid = String((JRS.CustomizerPopup && JRS.CustomizerPopup.getSessionId && JRS.CustomizerPopup.getSessionId()) || ''); } catch (e) {}
    _confirmAttempts[sid] = (_confirmAttempts[sid] || 0) + 1;
    return _confirmAttempts[sid];
  }

  // Which _friendlyBuildError branch a raw error lands on. 'generic_unmatched'
  // is the "we don't know" branch — a rising count of it is the signal that a
  // new failure mode has appeared.
  var _FRIENDLY_BRANCHES = [
    { key: 'bundle_sizes_missing', re: /per-size data|requires context\.bundle_children|no sizes|has no sizes/i },
    { key: 'position_variant_unavailable', re: /positionVariantId|Cannot find variant|no usable variant/i },
    { key: 'logo_setup_missing', re: /missing catalogue descriptor|logo assignment|unknown logo source/i }
  ];
  function _classifyBuildError(raw) {
    var m = String((raw && raw.message) || raw || '');
    for (var i = 0; i < _FRIENDLY_BRANCHES.length; i++) {
      if (_FRIENDLY_BRANCHES[i].re.test(m)) return _FRIENDLY_BRANCHES[i].key;
    }
    return 'generic_unmatched';
  }
  function _friendlyBranchFields(raw) {
    var branch = _classifyBuildError(raw);
    var out = { friendly_branch: branch, raw_error: String((raw && raw.message) || raw || '').slice(0, 200) };
    if (branch === 'generic_unmatched') {
      out.regexes_tested = 'bundle_sizes_missing,position_variant_unavailable,logo_setup_missing';
      out.regexes_matched = 0;
    }
    return out;
  }

  // Fire confirm_failed with the diagnostic block, reading /cart.js first so
  // cart_item_count_after distinguishes "nothing added" from "partly added".
  // Telemetry only — never blocks or alters the add path.
  function _fireConfirmFailed(fields, attemptIndex, submitResult) {
    var payload = {};
    for (var k in fields) { if (Object.prototype.hasOwnProperty.call(fields, k)) payload[k] = fields[k]; }
    payload.attempt_index = attemptIndex;
    if (submitResult) {
      if (typeof submitResult.httpStatus === 'number') payload.http_status = submitResult.httpStatus;
      if (submitResult.shopifyDescription) payload.shopify_description = String(submitResult.shopifyDescription).slice(0, 200);
      if (typeof submitResult.failedIndex === 'number') payload.failed_group_index = submitResult.failedIndex;
      if (typeof submitResult.groupCount === 'number') payload.group_count = submitResult.groupCount;
      if (submitResult.failedGroup) {
        var fgp = submitResult.failedGroup;
        // v8.1.4 — child_variant_id is the DECORATION line of the failing group (as
        // before); the item Shopify refused is the garment → failed_variant_id.
        payload.child_variant_id = fgp.childVariantId || (fgp.isGarment ? 0 : fgp.variantId) || 0;
        if (typeof payload.failed_variant_id === 'undefined') payload.failed_variant_id = fgp.garmentVariantId || fgp.variantId || 0;
        if (typeof payload.requested_qty === 'undefined') payload.requested_qty = fgp.garmentRequestedQty || fgp.requestedQty || 0;
        payload.failed_group_total_qty = fgp.totalQty || 0;
      }
      if (typeof payload.failure_class === 'undefined') {
        payload.failure_class = (submitResult.stockFailure && submitResult.stockFailure.failureClass) || 'other';
      }
      if (submitResult.pattern) payload.cart_submit_pattern = submitResult.pattern;
      if (typeof submitResult.groupSplits === 'number') payload.group_splits = submitResult.groupSplits; // v8.2.0
      payload.items_added_before_failure = (submitResult.addedItems || []).length;
    }
    var fire = function (cartCount, readStatus) {
      payload.cart_item_count_after = (typeof cartCount === 'number') ? cartCount : -1;
      // v8.1.1 — why the count is -1 when it is: the HTTP status of the
      // /cart.js read (429 = the same block that failed the add).
      payload.cart_read_status = readStatus;
      if (submitResult && submitResult.challenge) payload.shopify_challenge = true;
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_confirm_failed', payload);
    };
    try {
      if (JRS.CartAPI && typeof JRS.CartAPI.getCart === 'function') {
        JRS.CartAPI.getCart().then(function (cart) {
          fire(cart && typeof cart.item_count === 'number' ? cart.item_count : -1, 200);
        }).catch(function (readErr) { fire(-1, (readErr && typeof readErr.status === 'number') ? readErr.status : 0); });
        return;
      }
    } catch (e) {}
    fire(-1, -1);
  }

  var _mountEl = null;
  var _listeners = [];
  // Per-mount tracker for the Doc 4 §7 login nudge — prevents double-firing
  // pdp_customizer_login_nudge_shown if onEnter is called repeatedly.
  var _shownNudgesThisMount = {};

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var draft = state.draft || {};
    var ctx = _resolvePageContext(draft);
    var variant = _resolveVariant(state, ctx);

    _shownNudgesThisMount = {};
    // Phase 4 commit 6 — cart-edit mode rebrands the review chrome.
    // editMode is the canonical "active edit" binding written by the
    // popup-state reducer (session 1 commit 3 + bug-1 follow-up). When
    // null/false, render the standard PDP review chrome unchanged.
    var editMode = (state.editMode && state.editMode.active) ? state.editMode : null;
    _render(draft, ctx, variant, state.flow, editMode);
    _bindInteractions(editMode);
    _syncCta(editMode); // Confirm/Update CTA → sticky chrome footer; microcopy → reassurance line
    _firePostRenderShownPass();

    // Doc 3 v0.2 §4.9 — pdp_customizer_review_view. Includes a
    // `cart_edit_mode` flag so analytics can segment cart-edit review
    // arrivals from PDP review arrivals.
    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_review_view', {
      step_id: STEP_ID,
      flow: state.flow,
      position_count: _totalPositionCount(draft),
      logo_strategy: draft.logo_strategy || null,
      // Phase per_product S3 — segment review arrivals by bundle branding
      // strategy (additive; null for multistep). No new GA4 event.
      branding_strategy: draft.branding_strategy || null,
      variant: variant,
      cart_edit_mode: !!editMode
    });
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    // Cart-submit wiring uses the dedicated Confirm CTA click handler
    // below rather than the shell-generic primary CTA handler, so this
    // hook always returns valid:true. (The Confirm CTA's own click
    // handler runs the actual submit pipeline including error UX.)
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Context + variant resolution
  // ---------------------------------------------------------------------

  function _resolvePageContext(draft) {
    var pdpCtx = (window.JRS_PDP_CONTEXT && typeof window.JRS_PDP_CONTEXT === 'object')
      ? window.JRS_PDP_CONTEXT
      : {};

    // Phase 4 v2 Session 2 commit 3 — bundle cart-edit context merge.
    // On bundle PDP cart-add, window.JRS_PDP_CONTEXT carries
    // bundle_children + bundleProductVariantId + bundleProductHandle
    // (populated by jrs-v2-pdp-trigger.liquid at click time).
    // On bundle cart-edit, JRS_PDP_CONTEXT is empty / missing those
    // fields (the cart page isn't a bundle PDP), so the bundle
    // render variant has no data. The parser supplies the same
    // information on state.draft._bundle_meta + state.draft._bundle_
    // children — merge those in when present so the review's bundle
    // variant renders identically to PDP cart-add.
    //
    // Multistep cart-edit and PDP cart-add paths are unaffected:
    // draft is undefined / has no _bundle_meta in those cases.
    var bundleChildren = pdpCtx.bundle_children || null;
    var bundleProductVariantId = (typeof pdpCtx.bundleProductVariantId === 'number')
      ? pdpCtx.bundleProductVariantId
      : 0;
    var bundleProductHandle = pdpCtx.bundleProductHandle || '';
    var bundleProductTitleFromMeta = '';
    var bundleProductImageFromMeta = '';
    if (draft && draft._bundle_children && draft._bundle_children.length > 0) {
      bundleChildren = draft._bundle_children;
    }
    if (draft && draft._bundle_meta) {
      if (typeof draft._bundle_meta.bundleProductVariantId === 'number' &&
          draft._bundle_meta.bundleProductVariantId > 0) {
        bundleProductVariantId = draft._bundle_meta.bundleProductVariantId;
      }
      if (draft._bundle_meta.bundleProductHandle) {
        bundleProductHandle = draft._bundle_meta.bundleProductHandle;
      }
      // Phase 4 v2 bundle render polish — openFromCartLine now stashes
      // the clicked cart line's product_title + featured_image.url on
      // _bundle_meta for the chrome to read. When present, these are
      // canonical (sourced directly from Shopify's cart-line wrapper)
      // and take precedence over the humanised-handle fallback below.
      if (draft._bundle_meta.bundleProductTitle) {
        bundleProductTitleFromMeta = draft._bundle_meta.bundleProductTitle;
      }
      if (draft._bundle_meta.bundleProductImage) {
        bundleProductImageFromMeta = draft._bundle_meta.bundleProductImage;
      }
    }

    // Three-level priority for productTitle:
    //   1. pdpCtx.productTitle — the canonical PDP-context source.
    //      Populated on PDP cart-add by jrs-v2-pdp-trigger.liquid.
    //   2. bundleProductTitle from _bundle_meta — the cart-edit
    //      canonical source. Stashed by openFromCartLine from the
    //      clicked cart line's `product_title` field (Shopify's
    //      /cart.js response shape).
    //   3. Humanised handle — best-effort fallback when neither (1)
    //      nor (2) is available. Not byte-identical to Shopify's
    //      product.title but unambiguously bundle-named.
    //
    // Multistep cart-edit ALSO resolves via (1): the cart page is not a
    // PDP, so jrs-v2-cart-edit-flow.js `_hydratePdpContextForCartEdit`
    // (called unconditionally for every cart-edit at
    // jrs-v2-customizer-popup.js:725) merges the clicked cart line's
    // product_title + featured_image.url into window.JRS_PDP_CONTEXT via
    // _buildSyntheticContext. So pdpCtx.productTitle is populated for
    // multistep cart-edit too — the real title renders, not the 'Product'
    // placeholder. (Locked by step-review-context.test.js, 2026-07-06.)
    var productTitle = pdpCtx.productTitle || '';
    if (!productTitle && bundleProductTitleFromMeta) {
      productTitle = bundleProductTitleFromMeta;
    }
    if (!productTitle && bundleProductHandle) {
      productTitle = _humaniseHandle(bundleProductHandle);
    }

    // Product image for bundle cart-edit. Like productTitle, fall
    // back to _bundle_meta.bundleProductImage when pdpCtx.productImage
    // is empty (cart page has no bundle PDP context). When neither
    // source is available, the render falls to the existing SVG
    // placeholder.
    var productImage = pdpCtx.productImage || '';
    if (!productImage && bundleProductImageFromMeta) {
      productImage = bundleProductImageFromMeta;
    }

    return {
      productTitle: productTitle,
      productImage: productImage,
      productColour: pdpCtx.productColour || '',
      productPricing: pdpCtx.productPricing || null,
      sizes: (pdpCtx.sizes && pdpCtx.sizes.length > 0) ? pdpCtx.sizes : [],
      bundle_children: bundleChildren,
      // Blob A — the [data-jrs-bundle-data] bootstrap (categories[].positions[]
      // with the merchant's real per-position variants, incl. the FREE Left
      // Breast variant), read into window.JRS_PDP_CONTEXT.bundle_categories at
      // click time by jrs-v2-pdp-trigger.liquid:_readBundleCategories(). MUST be
      // forwarded: cart-submit re-derives the position/logo SKU from Blob A
      // (_buildCustomization + _buildPerProductBundleCategories) so a free-
      // configured position adds at £0. Omitting it makes both paths fall back to
      // the paid shop catalogue — the "free logo charged £8" bug. null on
      // multistep PDP / cart-edit (no bundle bootstrap) → catalogue fallback.
      bundle_categories: pdpCtx.bundle_categories || null,
      // Phase 3 — bundle-payload contract (Doc 2 §13.13) — forward the
      // bundle parent identifiers from window.JRS_PDP_CONTEXT (set by
      // jrs-v2-pdp-trigger.liquid:215-234). Without this propagation the
      // cart-payload-builder's getBundleVariantId fallback at
      // jrs-v2-cart-payload-builder.js:671-672 returns the first
      // sub-product's variant ID, producing a broken cart shape (one
      // sub-product as the main line item instead of the bundle parent).
      // See docs/phase-3-bundle-variant-id-diagnosis.md for the full
      // diagnosis. Multistep PDPs don't populate the bundle-specific
      // fields; the defaults below (0 / '') are the "missing" markers
      // getBundleVariantId already handles.
      bundleProductVariantId: bundleProductVariantId,
      bundleProductHandle: bundleProductHandle,
      bundleMetaobjectId: pdpCtx.bundleMetaobjectId || ''
    };
  }

  function _resolveVariant(state, ctx) {
    // PDP bundle add: state.flow === 'bundle' (set by JRS.FlowBundle.start).
    if (state.flow === 'bundle') return 'bundle';
    // Bundle cart-edit: state.flow === 'cart-edit', but the parsed
    // draft carries _bundle_meta (Phase 4 v2 Session 1 — _parseBundle
    // emits this on bundle line sets) AND/OR draft.flow === 'bundle'
    // (Session 2 commit 1 — the shell stamps the underlying source
    // flow on the draft post-parse). Either signal routes to the
    // bundle render variant so the review chrome matches the popup's
    // PDP-cart-add behaviour.
    if (state.draft && (state.draft._bundle_meta || state.draft.flow === 'bundle')) {
      return 'bundle';
    }
    if (ctx.sizes.length > 1) return 'multi_size_multistep';
    return 'single_size_multistep';
  }

  /**
   * Build the FROZEN display snapshot the terminal success step renders from
   * (FR3). Captured at confirm time so a post-confirm RESET can't blank the
   * receipt. Stage 2 is intentionally minimal — single-product title + bundle
   * flag — which proves the review→success pipe; Stages 3–4 expand it with
   * branding lines, the cart-meta row (CartAPI.getCart) and bundle child rows.
   * @param {Object} state - popup state at confirm
   * @param {Object} draft - state.draft
   * @param {Object} ctx - _resolvePageContext() result
   * @param {string} variant - _resolveVariant() result
   * @param {Object} result - JRS.CartSubmit.submitAndVerify resolution
   * @returns {Object} plain snapshot object
   */
  function _buildSuccessSnapshot(state, draft, ctx, variant, result) {
    var flow = (state && state.flow) || null;
    var pattern = (result && result.pattern) || null;
    // cartMeta is filled by the success step's onEnter via CartAPI.getCart()
    // after result.ok (decision 2 / D3) — NOT captured here, because the
    // authoritative cart read happens post-add, off the snapshot path.
    if (variant === 'bundle') {
      var bundleSnap = _buildBundleSnapshot(draft, ctx);
      bundleSnap.flow = flow;
      bundleSnap.pattern = pattern;
      return bundleSnap;
    }
    return {
      isBundle: false,
      variant: variant,
      flow: flow,
      product: _buildSnapshotProduct(ctx, variant),
      brandingLines: _buildSnapshotBrandingLines(draft),
      cartMeta: null,
      pattern: pattern
    };
  }

  /**
   * Product line for the success receipt: title, image, a customer-facing meta
   * line (colour · size · Qty N, or "N items" for multi-size) and the GBP price
   * label. Mirrors _renderProductCard's meta+price so the receipt agrees with
   * the review screen.
   */
  function _buildSnapshotProduct(ctx, variant) {
    var totalQty = _sumSizes(ctx.sizes) || 1;
    var pricing = ctx.productPricing;
    var priceLabel = (pricing && pricing.priceFormatted) ? pricing.priceFormatted : '';
    if (variant === 'multi_size_multistep' && pricing && typeof pricing.priceCents === 'number') {
      priceLabel = _formatMoney(pricing.priceCents * totalQty, pricing.currencyCode);
    }

    var metaParts = [];
    if (ctx.productColour) metaParts.push(ctx.productColour);
    if (variant === 'multi_size_multistep') {
      metaParts.push(totalQty + ' items');
    } else {
      if (ctx.sizes.length === 1 && ctx.sizes[0].sizeLabel) metaParts.push(ctx.sizes[0].sizeLabel);
      metaParts.push('Qty ' + totalQty);
    }

    return {
      title: ctx.productTitle || '',
      imageUrl: ctx.productImage || '',
      metaLine: metaParts.filter(Boolean).join(' · '),
      priceLabel: priceLabel
    };
  }

  /**
   * One receipt line per configured position (mockup .brand-line):
   * { positionLabel, methodLabel, logo }. Labels are customer-facing display
   * strings (PositionCatalogue name, _methodLabel) — never handles/ids (D4).
   */
  function _buildSnapshotBrandingLines(source, child, childSlot) {
    var positions = source.positions || [];
    var methods = source.application_methods || {};
    var assigns = source.logo_assignments || {};
    var lines = [];
    for (var i = 0; i < positions.length; i++) {
      var pid = positions[i];
      var descriptor = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getById === 'function')
        ? (JRS.PositionCatalogue.getById(pid) || { name: pid })
        : { name: pid };
      lines.push({
        positionLabel: _prettyPositionName(pid, descriptor),
        methodLabel: _methodLabel(_effectiveMethodForLine(pid, methods[pid], descriptor, child, childSlot)),
        logo: _snapshotLogo(assigns[pid])
      });
    }
    return lines;
  }

  /**
   * The method a receipt/summary line should DISPLAY. For a bundle_wide garment
   * (a `child` is passed) this resolves the child's OWN restriction via the same
   * engine cart-submit uses (JRS.MethodRestrictions.resolveCategoryMethod), so
   * the receipt shows exactly what was added to the cart — e.g. a print-only
   * garment reads "Print" even though the customer's single choice was
   * Embroidery. No child (single-product, or per_product where the source
   * already holds the resolved per-category method) → the chosen method as-is.
   */
  function _effectiveMethodForLine(pid, chosenMethod, descriptor, child, childSlot) {
    if (!child || !JRS.MethodRestrictions ||
        typeof JRS.MethodRestrictions.resolveCategoryMethod !== 'function') {
      return chosenMethod;
    }
    var variantIds = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getVariantIdsFor === 'function')
      ? JRS.PositionCatalogue.getVariantIdsFor(pid)
      : { embroidery: 0, print: 0 };
    // Slot context (2026-08-01, control parity) — mirror cart-submit's
    // slot-aware resolution EXACTLY, or the receipt shows a method the cart
    // didn't get (e.g. slot-3 outerwear forced to embroidery).
    var parentTags = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                      window.JRS_PDP_CONTEXT.productTags) || [];
    var parentMix = typeof JRS.MethodRestrictions.isMixAndMatchBundle === 'function' &&
                    JRS.MethodRestrictions.isMixAndMatchBundle(parentTags);
    var res = JRS.MethodRestrictions.resolveCategoryMethod(
      [child], pid, chosenMethod, variantIds, descriptor && descriptor.application_methods,
      { bundleSlot: (typeof childSlot === 'number') ? childSlot : null, mixAndMatch: parentMix });
    return (res && res.method) || chosenMethod;
  }

  /**
   * Logo chip descriptor for a position's assignment (D4):
   *   text  → { type:'text', label:'"LINE1 LINE2"', mini:'Aa' }
   *   file  → { type:'file', label:filename,        mini:'PNG' }
   * Returns null when a position has no assignment (defensive; shouldn't occur
   * for a branded confirm).
   */
  function _snapshotLogo(assign) {
    if (!assign) return null;
    if (assign.source === 'text') {
      var txt = String(assign.previewText || '').replace(/\s+/g, ' ').trim();
      return { type: 'text', label: txt ? '"' + txt + '"' : 'Custom text', mini: 'Aa' };
    }
    var filename = assign.filename || 'Logo';
    var ext = /\.([A-Za-z0-9]+)$/.exec(filename);
    return { type: 'file', label: filename, mini: ext ? ext[1].toUpperCase().slice(0, 4) : 'IMG' };
  }

  /**
   * Bundle receipt snapshot (Stage 4 / D2) — one row per configured garment
   * type. ctx.bundle_children is already grouped per sub-product with sizes[]
   * inside, so the Option B per-size cart split never reaches the UI: qty is
   * SUMMED across the type's sizes. Branding source per row:
   *   - bundle_wide  → the flat draft (same branding on every type)
   *   - per_product  → category_configs, matched child.productHandle → category
   * A type whose source yields no positions carries lines:[] (rendered
   * "No branding", excluded from the branded count). Price is
   * ctx.productPricing.priceFormatted — the SAME field the review product card
   * reads, so review and success can never disagree (D3); the cart-meta row
   * remains the authoritative cart truth via getCart.
   */
  function _buildBundleSnapshot(draft, ctx) {
    var children = (ctx && ctx.bundle_children) || [];
    var isPerProduct = !!(draft && draft.branding_strategy === 'per_product');
    var catByHandle = isPerProduct ? _bundleCategoryIndexByHandle() : null;
    var configs = (draft && draft.category_configs) || {};
    var logoKeys = {};
    var rows = [];
    var itemCount = 0;
    var brandedCount = 0;

    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var qty = _sumChildSizes(child);
      itemCount += qty;

      var source;
      if (isPerProduct) {
        var ci = catByHandle ? catByHandle[child.productHandle] : undefined;
        source = (ci !== undefined && configs[ci]) ? configs[ci] : null;
      } else {
        source = draft; // bundle_wide — flat draft applies to every type
      }

      // bundle_wide: resolve each garment's own method (pass `child` + its
      // 1-based slot so tag AND mix&match slot rules both apply — matching
      // cart-submit's slot-aware emission) so a print-only / embroidery-only
      // garment shows its real, cart-matching method. per_product:
      // category_configs already holds the resolved per-category method, so
      // no per-child resolution is needed.
      var lines = source
        ? _buildSnapshotBrandingLines(source, isPerProduct ? null : child, isPerProduct ? null : i + 1)
        : [];
      if (source) _collectLogoKeys(source, logoKeys);
      if (lines.length > 0) brandedCount++;

      rows.push({
        name: child.categoryName || child.productTitle || child.productHandle || 'Item',
        qty: qty,
        imageUrl: child.imageUrl || '',
        branded: lines.length > 0,
        lines: lines
      });
    }

    var distinctLogoCount = 0;
    for (var key in logoKeys) {
      if (Object.prototype.hasOwnProperty.call(logoKeys, key)) distinctLogoCount++;
    }

    return {
      isBundle: true,
      variant: 'bundle',
      flow: null, // set by caller
      product: {
        title: (ctx && ctx.productTitle) || '',
        imageUrl: (ctx && ctx.productImage) || '',
        priceLabel: (ctx && ctx.productPricing && ctx.productPricing.priceFormatted) || '',
        itemCount: itemCount,
        distinctLogoCount: distinctLogoCount
      },
      bundleRows: rows,
      brandedCount: brandedCount,
      totalTypes: rows.length,
      cartMeta: null,
      pattern: null // set by caller
    };
  }

  /** Sum a bundle child's per-size quantities → the single per-type qty pill. */
  function _sumChildSizes(child) {
    var sizes = (child && child.sizes) || [];
    var total = 0;
    for (var i = 0; i < sizes.length; i++) {
      var q = parseInt(sizes[i] && sizes[i].quantity, 10);
      if (isFinite(q) && q > 0) total += q;
    }
    return total;
  }

  /** { productHandle → categoryIndex } from the active bundle category list. */
  function _bundleCategoryIndexByHandle() {
    var map = {};
    var State = JRS.CustomizerPopupState;
    var list = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    for (var i = 0; i < (list || []).length; i++) {
      var cat = list[i];
      if (!cat || typeof cat.categoryIndex !== 'number') continue;
      var handles = cat.productHandles || [];
      for (var h = 0; h < handles.length; h++) {
        if (handles[h]) map[handles[h]] = cat.categoryIndex;
      }
    }
    return map;
  }

  /** Add each assigned position's logo identity to keySet (distinct-logo count). */
  function _collectLogoKeys(source, keySet) {
    var positions = (source && source.positions) || [];
    var assigns = (source && source.logo_assignments) || {};
    for (var i = 0; i < positions.length; i++) {
      var a = assigns[positions[i]];
      if (!a) continue;
      var key = a.logoId || ((a.source || '') + ':' + (a.filename || a.previewText || ''));
      if (key) keySet[key] = true;
    }
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _render(draft, ctx, variant, flow, editMode) {
    var positions = draft.positions || [];
    var isCartEdit = !!editMode;
    // Phase per_product S3 (spec D9) — per-product bundle review renders one
    // section per configured category (from draft.category_configs) instead of
    // the flat logo-group list. bundle_wide / multistep are untouched.
    var isPerProduct = _isPerProductReview(draft, variant);

    // Per-product bundles read as a bundle review ("Review your bundle");
    // bundle_wide / single / multistep keep the branding-focused title.
    // Cart-edit uses "Review your changes". Subtitle omitted — edit banner
    // carries the replace-item notice.
    var title = isCartEdit
      ? 'Review your changes'
      : (isPerProduct ? 'Review your bundle' : 'Review your logo setup');

    var logoGroups = _groupAssignmentsByLogo(positions, draft.logo_assignments || {});

    // Doc 4 §7.1 location 3 — guest-only login nudge on the review step.
    // Suppressed in cart-edit mode: the nudge is mid-funnel for new
    // customers, but a cart-edit user has already added an item so the
    // sign-in moment was earlier in the funnel (Decision Doc 4 §7.4).
    var isLoggedIn = (JRS.LogoService && typeof JRS.LogoService.isLoggedIn === 'function')
      ? !!JRS.LogoService.isLoggedIn()
      : false;
    var renderReviewNudge = !isCartEdit && !isLoggedIn && !_isNudgeDismissed('review');

    var rootClass = 'jrs-step jrs-step--review' + (isCartEdit ? ' is-cart-edit-mode' : '');
    var editModePill = isCartEdit
      ? '<span class="jrs-review__edit-mode-pill" aria-label="Edit mode active">EDIT MODE</span>'
      : '';
    // CTA label + microcopy are no longer rendered in-content — the Confirm
    // button lives in the sticky chrome footer (set via _syncCta → shell
    // setPrimaryCta) and the microcopy becomes the footer reassurance line.

    _mountEl.innerHTML =
      '<div class="' + rootClass + '" data-jrs-step="review">' +
           (isCartEdit ? _renderEditBanner() : '') +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title jrs-review__title">' +
             _escape(title) + editModePill +
      '    </h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
           // Per-product renders self-contained photo-led category cards, each
           // carrying its own product photo + name + branding — so the
           // bundle-level product hero and the flat "In your bundle" summary
           // are suppressed (they'd duplicate the cards). bundle_wide keeps
           // both: its single hero + flat list ARE its summary.
           (isPerProduct ? '' : _renderProductCard(ctx, variant, isCartEdit)) +
           (variant === 'multi_size_multistep' ? _renderSizeList(ctx) : '') +
           (variant === 'bundle' && !isPerProduct ? _renderBundleSummary(ctx) : '') +
           (isPerProduct
             ? _renderPerProductSections(draft, ctx)
             : _renderLogoGroups(logoGroups, draft, ctx)) +
           (isCartEdit ? '' : _renderTrustBand()) +
           (renderReviewNudge
             ? _renderLoginNudge('review', 'Sign in to save this configuration to your account.', {})
             : '') +
      '    <p data-jrs-review-error class="jrs-step__error" hidden role="alert"></p>' +
      '  </section>' +
      '</div>';
  }

  /**
   * Cart-edit banner. Doc 6 §10.1 + mockups/cart-edit-review-desktop.html:
   * full-width above the body grid; icon + title + sub + Cancel changes
   * button. Sits inside .jrs-step--review so its descendant selector
   * .is-cart-edit-mode .jrs-edit-banner can style it without leaking to
   * other steps.
   */
  function _renderEditBanner() {
    return (
      '<div class="jrs-edit-banner" role="region" aria-label="Edit mode">' +
      '  <span class="jrs-edit-banner__icon" aria-hidden="true">' +
      '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '      <path d="M14 4 L20 10 L8 22 L2 22 L2 16 Z"/>' +
      '      <path d="M12 6 L18 12"/>' +
      '    </svg>' +
      '  </span>' +
      '  <div class="jrs-edit-banner__content">' +
      '    <p class="jrs-edit-banner__title">Editing a cart item — saving replaces it.</p>' +
      '  </div>' +
      '  <button class="jrs-edit-banner__cancel" type="button" ' +
      '          data-jrs-action="cart-edit-cancel" ' +
      '          aria-label="Cancel changes and return to cart">Cancel changes</button>' +
      '</div>'
    );
  }

  function _renderProductCard(ctx, variant, isCartEdit) {
    var imageHtml = ctx.productImage
      ? '<img class="jrs-product-card__img" src="' + _escape(ctx.productImage) + '" alt="" loading="lazy">'
      : '<svg class="jrs-product-card__img-fallback" viewBox="0 0 88 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '  <path d="M22 8 L14 16 L14 30 L22 30 L22 92 L66 92 L66 30 L74 30 L74 16 L66 8 L52 8 C52 13 36 13 36 8 Z" fill="#FFFFFF" stroke="#FFFFFF" stroke-width="1.5" stroke-linejoin="round" opacity="0.95"/>' +
        '  <path d="M38 8 L44 18 L50 8" fill="none" stroke="#1A2A38" stroke-width="1.5" stroke-linejoin="round"/>' +
        '</svg>';

    var totalQty = _sumSizes(ctx.sizes) || 1;
    var pricing = ctx.productPricing;
    var priceFormatted = (pricing && pricing.priceFormatted) ? pricing.priceFormatted : '';
    var totalPriceLabel = priceFormatted;
    if (variant === 'multi_size_multistep' && pricing && typeof pricing.priceCents === 'number') {
      totalPriceLabel = _formatMoney(pricing.priceCents * totalQty, pricing.currencyCode);
    }

    var metaParts = [];
    // Bundle hero: no colour/size meta line. The bundle's default variant
    // colour reads as "Default Title" and the per-child summary below already
    // carries the detail, so "Default Title · N-item bundle" is just noise.
    if (variant !== 'bundle') {
      if (ctx.productColour) metaParts.push(ctx.productColour);
      if (variant === 'single_size_multistep') {
        // Size: prefer the picked size (multistep add), else the cart line's Size
        // option (cart-edit, where ctx.sizes can be empty).
        var sizeLabel = (ctx.sizes.length === 1 ? (ctx.sizes[0].sizeLabel || '') : '') || ctx.productSize || '';
        if (sizeLabel) metaParts.push(sizeLabel);
        // Quantity: real total (sum of picked sizes), else the cart line quantity
        // (cart-edit), else 1 — never the old hardcoded "Quantity 1".
        var metaQty = _sumSizes(ctx.sizes) || ctx.lineItemQuantity || 1;
        metaParts.push('Quantity ' + metaQty);
      } else if (variant === 'multi_size_multistep') {
        metaParts.push(totalQty + ' items');
      }
    }
    var metaText = metaParts.filter(Boolean).join(' · ');

    return (
      '<section class="jrs-product-card" aria-label="Product being customised">' +
      '  <span class="jrs-product-card__image" aria-hidden="true">' + imageHtml + '</span>' +
      '  <div class="jrs-product-card__content">' +
      '    <p class="jrs-product-card__title">' + _escape(ctx.productTitle || 'Product') + '</p>' +
           (metaText
             ? '<p class="jrs-product-card__meta">' + _escape(metaText) + '</p>'
             : '') +
           (totalPriceLabel
             ? '<div class="jrs-product-card__price-row">' +
               '  <span class="jrs-product-card__price">' + _escape(totalPriceLabel) + '</span>' +
               '  <span class="jrs-product-card__price-vat">+VAT</span>' +
               '</div>'
             : '') +
      '  </div>' +
           // Phase 4 commit 6 — hide the product-card Change button in
           // cart-edit mode. The variant/size is LOCKED in cart-edit
           // (Doc 9 §4.5 / §4.3) — only positions/methods/logos are
           // editable. Re-rendering the Change CTA would suggest a
           // size swap is possible when it isn't.
           (isCartEdit
             ? ''
             : '<button type="button" class="jrs-product-card__edit"' +
               '          data-jrs-action="edit" data-jrs-edit-target="position_picker"' +
               '          aria-label="Change positions">Change</button>') +
      '</section>'
    );
  }

  function _renderSizeList(ctx) {
    var rows = '';
    var pricing = ctx.productPricing;
    var perItemPrice = (pricing && typeof pricing.priceCents === 'number')
      ? pricing.priceCents
      : null;

    for (var i = 0; i < ctx.sizes.length; i++) {
      var s = ctx.sizes[i];
      var subtotal = (perItemPrice !== null)
        ? _formatMoney(perItemPrice * (s.quantity || 0), pricing.currencyCode)
        : '';
      rows +=
        '<li class="jrs-size-row">' +
        '  <p class="jrs-size-row__name">' + _escape(s.sizeLabel || '') + '</p>' +
        '  <span class="jrs-size-row__qty">' + (s.quantity || 0) + '</span>' +
           (subtotal
             ? '<span class="jrs-size-row__subtotal">' + _escape(subtotal) + '</span>'
             : '') +
        '</li>';
    }

    return (
      '<section class="jrs-size-section" aria-label="Sizes and quantities">' +
      '  <ul class="jrs-size-list" role="list">' + rows + '</ul>' +
      '</section>'
    );
  }

  function _renderBundleSummary(ctx) {
    if (!ctx.bundle_children || ctx.bundle_children.length === 0) {
      return '';
    }
    var items = '';
    for (var i = 0; i < ctx.bundle_children.length; i++) {
      var child = ctx.bundle_children[i];
      // Phase 4 v2 Session 2 hotfix — read both the parser-emitted
      // shape (categoryName, set by _parseBundle from cart children's
      // _BundleItemName / _BundleCategoryName) and the PDP-context
      // shape (productTitle, populated by _readBundleChildren on
      // bundle PDPs). Cart-edit hits the categoryName path; PDP
      // cart-add hits the productTitle path. Either resolves to the
      // sub-product's display name; the 'Item' fallback only fires
      // when both are absent (malformed bundle).
      var displayName = child.categoryName || child.productTitle || 'Item';
      items += _renderBundleSummaryItem(child, displayName);
    }
    return (
      '<section class="jrs-bundle-summary" aria-label="Bundle contents">' +
      '  <h3 class="jrs-bundle-summary__title">In your bundle</h3>' +
      '  <ul class="jrs-bundle-summary__list" role="list">' + items + '</ul>' +
      '</section>'
    );
  }

  /**
   * Render a single bundle-summary list item with the sub-product's
   * name, colour, and per-size quantity breakdown.
   *
   * Parser-emitted child shape (state.draft._bundle_children[i]):
   *   { categoryName, categoryIndex, productCount, products[],
   *     sizes: [{variantId, sizeLabel, quantity, sku, colour, productHandle}] }
   *
   * PDP-context child shape (window.JRS_PDP_CONTEXT.bundle_children[i]):
   *   { productTitle, productHandle, colour, imageUrl,
   *     sizes: [{size, quantity, variantId, sku}] }
   *
   * Reads both shapes defensively:
   *   - displayName: parser's categoryName OR PDP's productTitle
   *   - colour: parser puts it on size entries (first entry is
   *     representative); PDP puts it at child level
   *   - sizes: both shapes carry sizes[]; parser uses sizeLabel +
   *     quantity, PDP uses size + quantity. Read either.
   */
  function _renderBundleSummaryItem(child, displayName) {
    // Group the per-size entries by their OWN colour (a child can mix colours —
    // e.g. some Orange + some Yellow vests share one product card). The old code
    // took a single colour (child.colour / first size) and listed every size
    // flat beneath it, so the Orange sizes were mislabelled Yellow. One meta line
    // per colour: "Yellow · S × 2, M × 2" / "Orange · L × 20, XL × 2".
    var groups = _groupChildSizesByColour(child);
    var totalQty = 0;
    var metaHtml = '';
    for (var g = 0; g < groups.length; g++) {
      var grp = groups[g];
      totalQty += grp.total;
      var parts = [];
      for (var s = 0; s < grp.sizes.length; s++) {
        var sz = grp.sizes[s];
        parts.push(sz.label ? (_escape(sz.label) + ' × ' + sz.qty) : ('× ' + sz.qty));
      }
      // "Colour · sizes"; drop the colour segment when unknown.
      var lineParts = [];
      if (grp.colour) lineParts.push(_escape(grp.colour));
      if (parts.length) lineParts.push(parts.join(', '));
      var lineText = lineParts.join(' · ');
      if (lineText) metaHtml += '<p class="jrs-bundle-item__meta">' + lineText + '</p>';
    }

    var qtyBadge = totalQty > 0
      ? '<span class="jrs-bundle-item__qty" aria-label="Quantity ' + totalQty + '">×' + totalQty + '</span>'
      : '';

    return (
      '<li class="jrs-bundle-item">' +
      '  <div class="jrs-bundle-item__content">' +
      '    <p class="jrs-bundle-item__name">' + _escape(displayName) + '</p>' +
      metaHtml +
      '  </div>' +
      qtyBadge +
      '</li>'
    );
  }

  /**
   * Title-case a kebab-case handle. Strips trailing "-bundle" suffix
   * when present so '6-item-kickstarter-bundle' renders as '6 Item
   * Kickstarter Bundle' (capitalises Bundle), not '6 Item Kickstarter
   * Bundle Bundle'. Defensive empty-input handling.
   *
   * Used by _resolvePageContext as a best-effort fallback when the
   * cart-edit chrome lacks a real bundle product title (Phase 4 v2
   * Session 2 hotfix — see _resolvePageContext for the full context).
   *
   * @param {string} handle
   * @returns {string}
   */
  function _humaniseHandle(handle) {
    if (!handle || typeof handle !== 'string') return '';
    var parts = handle.split('-');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var word = parts[i];
      if (!word) continue;
      out.push(word.charAt(0).toUpperCase() + word.slice(1));
    }
    return out.join(' ');
  }

  function _renderLogoGroups(groups, draft, ctx) {
    var html = '';
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      html += _renderLogoGroup(group, draft, ctx, g);
    }
    return html;
  }

  // 'beanie_front' → 'Beanie Front'. Fallback when the catalogue has no proper
  // display name for a position id (e.g. a position not in the definitions on
  // the current page — cart-edit / a headwear set).
  function _humanizePositionId(pid) {
    return String(pid || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  // Customer-facing position name: the catalogue's display name when it's a real
  // label, otherwise a humanised id — so the review/receipt never shows a raw
  // handle like "beanie_front". A name that equals the id or still looks like a
  // handle (contains "_") is treated as missing.
  function _prettyPositionName(pid, descriptor) {
    var nm = descriptor && descriptor.name;
    if (nm && String(nm) !== String(pid) && String(nm).indexOf('_') === -1) return String(nm);
    return _humanizePositionId(pid);
  }

  function _renderLogoGroup(group, draft, ctx, groupIndex, suppressEdit, perProduct) {
    // group: { logoId, source, label, filename, positions: [pid, ...] }
    // suppressEdit (per_product sections, S3): omit the per-position Edit and
    // per-group Change buttons — the per-category section carries a single
    // section-level Edit instead. bundle_wide / multistep pass it falsy so the
    // inline edit affordances render unchanged.
    // perProduct (Stage 2B): switches the eyebrow + method copy to the
    // photo-card form — sentence-case "Your logo" with "· used in N positions"
    // only when N>1, and past-tense method pills ("Embroidered"/"Printed").
    // The `data-jrs-method` attribute lets the per-product CSS tint each pill;
    // bundle_wide ignores it (its method stays the plain uppercase label).
    var positionRows = '';
    for (var i = 0; i < group.positions.length; i++) {
      var pid = group.positions[i];
      var descriptor = (JRS.PositionCatalogue && JRS.PositionCatalogue.getById(pid)) || { id: pid, name: pid };
      var method = (draft.application_methods || {})[pid];
      var methodLabel = _methodLabel(method, perProduct);
      positionRows +=
        '<li class="jrs-position-row">' +
        '  <p class="jrs-position-row__name">' + _escape(_prettyPositionName(pid, descriptor)) + '</p>' +
        '  <span class="jrs-position-row__method" data-jrs-method="' + _escape(method || '') + '">' + _escape(methodLabel) + '</span>' +
        (suppressEdit ? '' :
        '  <button type="button" class="jrs-position-row__edit"' +
        '          data-jrs-action="edit" data-jrs-edit-target="application_method"' +
        '          data-jrs-edit-position="' + _escape(pid) + '"' +
        '          aria-label="Edit ' + _escape(descriptor.name) + ' position">Edit</button>') +
        '</li>';
    }

    // What the shopper sees is a label, not the uploaded filename — uploads are
    // stored under a content hash ("dc04ab66…cdce62.jpg"), which means nothing
    // to them, and the thumbnail beside it already identifies the logo. Reads
    // the assignment's existing `source` ('text' | 'upload' | 'existing'), the
    // same signal _renderLogoThumb branches on below; saved and uploaded logos
    // are both "Your logo". group.filename itself is untouched and still feeds
    // the thumbnail's document-badge check.
    var filename = group.source === 'text' ? 'Your text logo' : 'Your logo';

    // 2026-05-20 Bug 6 fix — logo group's "Change" carries the first
    // position-id in the group so logo_picker can rehydrate the right
    // assignment on re-entry. Without this, logo_picker reads whatever
    // `_current_position_for_logo` was set to by the orchestrator at
    // the END of the original flow (the LAST position iterated, not
    // the one belonging to the edited logo group).
    var firstPid = group.positions.length > 0 ? group.positions[0] : '';
    return (
      '<section class="jrs-logo-group" aria-label="Logo applied to ' + group.positions.length + ' positions">' +
      '  <div class="jrs-logo-group__header">' +
      '    <span class="jrs-logo-group__thumb" aria-hidden="true">' +
            _renderLogoThumb(group) +
      '    </span>' +
      '    <div class="jrs-logo-group__content">' +
      '      <p class="jrs-logo-group__filename" title="' + _escape(filename) + '">' + _escape(filename) + '</p>' +
      '    </div>' +
      (suppressEdit ? '' :
      '    <button type="button" class="jrs-logo-group__edit"' +
      '            data-jrs-action="edit" data-jrs-edit-target="logo_picker"' +
      '            data-jrs-edit-position="' + _escape(firstPid) + '">Change</button>') +
      '  </div>' +
      '  <ul class="jrs-position-list" role="list">' + positionRows + '</ul>' +
      '</section>'
    );
  }

  function _renderLogoThumb(group) {
    if (group.source === 'text') {
      return '<span class="jrs-logo-group__thumb-text">' + _escape(group.previewText || 'TEXT') + '</span>';
    }
    if (group.url) {
      // PDF (or any non-image) logo: an <img> can't render it — show the
      // shared document badge (JRS.LogoService, control-theme parity
      // 2026-08-01) labelled with the extension, same as the picker /
      // bundle summary / cart page.
      if (JRS.LogoService && typeof JRS.LogoService.isDisplayableImage === 'function' &&
          !JRS.LogoService.isDisplayableImage(group.filename || group.url)) {
        return '<img alt="" loading="lazy" src="' +
          _escape(JRS.LogoService.nonImageThumb(group.filename || group.url)) + '">';
      }
      // A broken/expired logo URL degrades to the inline LOGO box rather than
      // a broken-image glyph (the box is identical to the no-url fallback).
      return '<img alt="" loading="lazy" src="' + _escape(group.url) + '"' +
        ' onerror="this.onerror=null;this.outerHTML=window.JRS.Step.Review._logoBoxSvg()">';
    }
    return _logoBoxSvg();
  }

  // Inline placeholder for a missing/broken logo image. Exposed so the
  // _renderLogoThumb onerror handler can swap a failed <img> for it.
  function _logoBoxSvg() {
    return '<svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<rect x="0" y="0" width="100" height="60" fill="#FFFFFF"/>' +
      '<rect x="20" y="20" width="60" height="20" fill="#1A1A1A"/>' +
      '<text x="50" y="34" text-anchor="middle" font-family="Poppins" font-size="10" font-weight="700" fill="#FFFFFF">LOGO</text>' +
      '</svg>';
  }

  // Inline garment outline for a missing/broken per-product photo. currentColor
  // lets the .jrs-review-category__photo panel tint it to a muted placeholder.
  function _garmentSvg() {
    return '<svg class="jrs-review-category__img-fallback" viewBox="0 0 88 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M22 8 L14 16 L14 30 L22 30 L22 92 L66 92 L66 30 L74 30 L74 16 L66 8 L52 8 C52 13 36 13 36 8 Z" fill="currentColor"/>' +
      '<path d="M38 8 L44 18 L50 8" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>' +
      '</svg>';
  }

  // ---------------------------------------------------------------------
  // Per-product review sections (Phase per_product S3, spec D9)
  // ---------------------------------------------------------------------

  // True when the review should render per-category sections: a bundle flow
  // with branding_strategy 'per_product' and >=1 configured category.
  function _isPerProductReview(draft, variant) {
    if (variant !== 'bundle') return false;
    if (!draft || draft.branding_strategy !== 'per_product') return false;
    return _categoryConfigKeys(draft.category_configs).length > 0;
  }

  // Numeric-sorted category_configs keys (the real metaobject indices).
  function _categoryConfigKeys(configs) {
    if (!configs) return [];
    var keys = [];
    for (var k in configs) {
      if (Object.prototype.hasOwnProperty.call(configs, k)) {
        var n = parseInt(k, 10);
        if (!isNaN(n) && n >= 0) keys.push(n);
      }
    }
    keys.sort(function (a, b) { return a - b; });
    return keys;
  }

  // Total configured positions. For per_product this sums across
  // category_configs (the flat draft holds only the last category's scratch
  // after the loop), keeping the review_view / confirm telemetry honest; else
  // it's the flat draft length (bundle_wide / multistep unchanged).
  function _totalPositionCount(draft) {
    if (draft && draft.branding_strategy === 'per_product' && draft.category_configs) {
      var keys = _categoryConfigKeys(draft.category_configs);
      var total = 0;
      for (var i = 0; i < keys.length; i++) {
        total += (((draft.category_configs[keys[i]] || {}).positions) || []).length;
      }
      return total;
    }
    return ((draft && draft.positions) || []).length;
  }

  // Category metadata (name + product handles) keyed by real category index,
  // from JRS.CustomizerPopupState.getActiveCategoryList() at runtime. Returns
  // {} when the active list is unavailable (cart-edit reopen is S4); names
  // then fall back to "Product N".
  function _activeCategoryMeta() {
    var meta = {};
    var State = JRS.CustomizerPopupState;
    var list = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    if (!list || !list.length) return meta;
    for (var i = 0; i < list.length; i++) {
      var cat = list[i];
      if (cat && typeof cat.categoryIndex === 'number') {
        meta[cat.categoryIndex] = {
          name: cat.name || '',
          productHandles: cat.productHandles || []
        };
      }
    }
    return meta;
  }

  // Sub-product display names for a category — match bundle_children to the
  // category's product handles. Empty array when nothing resolves.
  function _categorySubProducts(handles, bundleChildren) {
    if (!handles || !handles.length || !bundleChildren || !bundleChildren.length) return [];
    var byHandle = {};
    for (var h = 0; h < handles.length; h++) byHandle[handles[h]] = true;
    var names = [];
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (child && byHandle[child.productHandle]) {
        names.push(child.productTitle || child.productHandle || '');
      }
    }
    return names;
  }

  // First product photo for a category — the imageUrl of the first matched
  // bundle child (PDP-context children carry imageUrl; parser/cart-edit
  // children don't, so this returns '' there and the card draws the garment
  // fallback). Stage 2B.
  function _categoryImage(handles, bundleChildren) {
    if (!handles || !handles.length || !bundleChildren || !bundleChildren.length) return '';
    var byHandle = {};
    for (var h = 0; h < handles.length; h++) byHandle[handles[h]] = true;
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (child && byHandle[child.productHandle] && child.imageUrl) return child.imageUrl;
    }
    return '';
  }

  // First product photo for a category matched by categoryIndex — the parser's
  // cart-edit children are category-grouped (categoryIndex + a category-level
  // imageUrl filled from _bundle_raw), NOT the PDP-flat shape `_categoryImage`
  // matches on productHandle. Used as the cart-edit fallback so the per-product
  // review shows the real garment photo instead of the placeholder.
  function _categoryImageByIndex(bundleChildren, categoryIndex) {
    if (!bundleChildren || !bundleChildren.length) return '';
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (child && Number(child.categoryIndex) === Number(categoryIndex) && child.imageUrl) {
        return child.imageUrl;
      }
    }
    return '';
  }

  // Real product name for a category matched by categoryIndex — the parser fills
  // `productName` on cart-edit children from _bundle_raw. Preferred over the
  // generic categoryName ("Product 1") for the per-product section header.
  function _categoryProductNameByIndex(bundleChildren, categoryIndex) {
    if (!bundleChildren || !bundleChildren.length) return '';
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (child && Number(child.categoryIndex) === Number(categoryIndex) && child.productName) {
        return child.productName;
      }
    }
    return '';
  }

  // Total garment quantity for a category — sums child sizes[].quantity across
  // every matched child. Returns 0 when no sizes resolve (parser/cart-edit
  // children), in which case the card omits the "· ×N" qty. Stage 2B.
  function _categoryQty(handles, bundleChildren) {
    if (!handles || !handles.length || !bundleChildren || !bundleChildren.length) return 0;
    var byHandle = {};
    for (var h = 0; h < handles.length; h++) byHandle[handles[h]] = true;
    var total = 0;
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (!child || !byHandle[child.productHandle]) continue;
      var sizes = child.sizes || [];
      for (var s = 0; s < sizes.length; s++) {
        var q = parseInt(sizes[s].quantity, 10);
        if (isFinite(q) && q > 0) total += q;
      }
    }
    return total;
  }

  // Matched bundle children for a category. PDP path matches on productHandle
  // (active-list handles); cart-edit parser children carry categoryIndex instead
  // (no PDP context → empty handle set), so fall back to a categoryIndex match.
  // Mutually exclusive in practice (PDP children have no categoryIndex; cart-edit
  // children don't appear in the active-list handle set).
  function _categoryChildren(handles, bundleChildren, categoryIndex) {
    if (!bundleChildren || !bundleChildren.length) return [];
    var byHandle = {};
    if (handles && handles.length) {
      for (var h = 0; h < handles.length; h++) byHandle[handles[h]] = true;
    }
    var out = [];
    for (var c = 0; c < bundleChildren.length; c++) {
      var child = bundleChildren[c];
      if (!child) continue;
      if (byHandle[child.productHandle]) out.push(child);
      else if (typeof child.categoryIndex === 'number' &&
               Number(child.categoryIndex) === Number(categoryIndex)) out.push(child);
    }
    return out;
  }

  // Group a child's per-size entries by colour, preserving first-seen order.
  // Each size entry carries its own colour (multi-colour-per-child is supported —
  // see jrs-v2-pdp-trigger _readBundleChildren); fall back to the child-level
  // colour. Reads sizeLabel (parser) or size (PDP). Returns
  // [{ colour, sizes:[{label,qty}], total }], skipping zero/blank quantities.
  function _groupChildSizesByColour(child) {
    var sizes = (child && child.sizes) || [];
    var order = [];
    var byColour = {};
    for (var i = 0; i < sizes.length; i++) {
      var sz = sizes[i];
      var qty = parseInt(sz.quantity, 10);
      if (!isFinite(qty) || qty <= 0) continue;
      var colour = sz.colour || (child && child.colour) || '';
      var key = colour || '__none__';
      if (!byColour[key]) {
        byColour[key] = { colour: colour, sizes: [], total: 0 };
        order.push(key);
      }
      byColour[key].sizes.push({ label: sz.sizeLabel || sz.size || '', qty: qty });
      byColour[key].total += qty;
    }
    var out = [];
    for (var o = 0; o < order.length; o++) out.push(byColour[order[o]]);
    return out;
  }

  // Collapsible per-category size + colour breakdown (collapsed by default).
  // Native <details> — no JS wiring, accessible, resets to collapsed on
  // re-render (which is the desired default). Returns '' when no sizes resolve
  // (e.g. cart-edit children with no size data) so no empty disclosure shows.
  function _renderCategorySizeDisclosure(children) {
    if (!children || !children.length) return '';
    var multi = children.length > 1;
    var blocks = '';
    var totalQty = 0;
    for (var c = 0; c < children.length; c++) {
      var child = children[c];
      var byColour = _groupChildSizesByColour(child);
      if (!byColour.length) continue;
      var colourRows = '';
      for (var g = 0; g < byColour.length; g++) {
        var grp = byColour[g];
        totalQty += grp.total;
        var pills = '';
        for (var s = 0; s < grp.sizes.length; s++) {
          pills +=
            '<li class="jrs-cat-sizes__size">' +
            '<span class="jrs-cat-sizes__size-label">' + _escape(grp.sizes[s].label || '—') + '</span>' +
            '<span class="jrs-cat-sizes__size-qty">&times;' + grp.sizes[s].qty + '</span>' +
            '</li>';
        }
        colourRows +=
          '<div class="jrs-cat-sizes__colour-group">' +
          (grp.colour
            ? '<span class="jrs-cat-sizes__colour">' + _escape(grp.colour) + '</span>'
            : '') +
          '<ul class="jrs-cat-sizes__sizes" role="list">' + pills + '</ul>' +
          '</div>';
      }
      blocks +=
        '<div class="jrs-cat-sizes__product">' +
        (multi && child.productTitle
          ? '<p class="jrs-cat-sizes__product-name">' + _escape(child.productTitle) + '</p>'
          : '') +
        colourRows +
        '</div>';
    }
    if (!blocks) return '';
    var countLabel = totalQty > 0 ? (totalQty + (totalQty === 1 ? ' item' : ' items')) : '';
    return (
      '<details class="jrs-cat-sizes" data-jrs-cat-sizes>' +
      '  <summary class="jrs-cat-sizes__summary">' +
      '    <svg class="jrs-cat-sizes__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9 L12 15 L18 9"/></svg>' +
      '    <span class="jrs-cat-sizes__title">Sizes &amp; colours</span>' +
      (countLabel ? '    <span class="jrs-cat-sizes__count">' + countLabel + '</span>' : '') +
      '  </summary>' +
      '  <div class="jrs-cat-sizes__body">' + blocks + '</div>' +
      '</details>'
    );
  }

  function _renderPerProductSections(draft, ctx) {
    var configs = (draft && draft.category_configs) || {};
    var keys = _categoryConfigKeys(configs);
    if (!keys.length) return '';
    var meta = _activeCategoryMeta();
    var children = (ctx && ctx.bundle_children) || [];
    // Cart-edit reopen (S4): getActiveCategoryList() is empty (no PDP context),
    // so resolve category names from the parser's _bundle_children, which carry
    // categoryName + categoryIndex (forward/PDP children don't, so this lookup
    // is empty on PDP and falls through to the active-list name).
    var childNames = _categoryNamesFromChildren(children);
    var html = '';
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var cfg = configs[k] || {};
      var info = meta[k] || {};
      // Real product name (parser fills it from _bundle_raw on cart-edit) beats
      // the generic category label ("Product 1") and the humanised fallback.
      var name = _categoryProductNameByIndex(children, k) || info.name || childNames[k] || ('Product ' + (k + 1));
      var subProducts = _categorySubProducts(info.productHandles, children);
      // PDP path matches on productHandle; cart-edit path (parser children) falls
      // back to a categoryIndex match against the _bundle_raw-sourced imageUrl.
      var image = _categoryImage(info.productHandles, children) || _categoryImageByIndex(children, k);
      var qty = _categoryQty(info.productHandles, children);
      var catChildren = _categoryChildren(info.productHandles, children, k);
      html += _renderPerProductSection(k, name, cfg, subProducts, image, qty, catChildren);
    }
    return html;
  }

  // Cart-edit category-name lookup: { categoryIndex → categoryName } from the
  // parser's _bundle_children (per-category entries with categoryName +
  // categoryIndex). Empty on forward/PDP (those children are per-product and
  // carry no category fields).
  function _categoryNamesFromChildren(children) {
    var out = {};
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      if (c && typeof c.categoryIndex === 'number' && c.categoryName) {
        out[c.categoryIndex] = c.categoryName;
      }
    }
    return out;
  }

  function _renderPerProductSection(categoryIndex, categoryName, cfg, subProducts, image, qty, children) {
    // Photo-led per-category card (Stage 2B). Reuses _renderLogoGroup with
    // suppressEdit=true (no per-position inline edits — the section-level Edit
    // round-trips the whole category, spec D9) and perProduct=true (photo-card
    // copy: sentence-case logo labels + method pills). The per-category draft
    // view carries only application_methods, which is all _renderLogoGroup reads.
    var perCatDraft = { application_methods: cfg.application_methods || {} };
    var groups = _groupAssignmentsByLogo(cfg.positions || [], cfg.logo_assignments || {});
    var groupsHtml = '';
    for (var g = 0; g < groups.length; g++) {
      groupsHtml += _renderLogoGroup(groups[g], perCatDraft, null, g, /* suppressEdit */ true, /* perProduct */ true);
    }

    // Photo panel: real child image when we have one, else the garment outline
    // with a "Placeholder image" tag. A broken image URL degrades to the same
    // garment outline via onerror.
    var hasImage = !!image;
    var photoInner = hasImage
      ? '<img class="jrs-review-category__img" src="' + _escape(image) + '" alt="" loading="lazy"' +
        ' onerror="this.onerror=null;this.outerHTML=window.JRS.Step.Review._garmentSvg()">'
      : _garmentSvg() + '<span class="jrs-review-category__tag">Placeholder image</span>';

    // Header: category eyebrow + product name(s) + qty + section Edit. When no
    // sub-products resolve (cart-edit reopen, S4), the category name becomes the
    // name line and the eyebrow is dropped.
    var hasSub = !!(subProducts && subProducts.length);
    var nameText = hasSub ? subProducts.join(', ') : categoryName;
    var eyebrowHtml = hasSub
      ? '<p class="jrs-review-category__eyebrow">' + _escape(categoryName) + '</p>'
      : '';
    var qtyHtml = (qty && qty > 0)
      ? ' <span class="jrs-review-category__qty">&middot; &times;' + qty + '</span>'
      : '';

    return (
      '<section class="jrs-review-category" data-jrs-review-section-category="' + categoryIndex + '">' +
      '  <div class="jrs-review-category__photo">' + photoInner + '</div>' +
      '  <div class="jrs-review-category__header">' +
      '    <div class="jrs-review-category__heading">' +
             eyebrowHtml +
      '      <p class="jrs-review-category__name" data-jrs-review-category-name>' + _escape(nameText) + qtyHtml + '</p>' +
      '    </div>' +
      '    <button type="button" class="jrs-review-category__edit"' +
      '            data-jrs-action="edit-category" data-jrs-category-index="' + categoryIndex + '"' +
      '            aria-label="Edit ' + _escape(categoryName) + ' branding">Edit' +
             _chevronSvg() +
      '    </button>' +
      '  </div>' +
           _renderCategorySizeDisclosure(children) +
           groupsHtml +
      '</section>'
    );
  }

  function _chevronSvg() {
    return '<svg class="jrs-review-category__edit-icon" viewBox="0 0 24 24" fill="none"' +
      ' stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
      ' aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';
  }

  function _renderTrustBand() {
    return (
      '<div class="jrs-trust-band" role="region" aria-label="Production guarantees">' +
      '  <div class="jrs-trust-band__row">' +
           _checkmarkSvg() +
      '    <span><strong>Free design proof</strong> emailed for your approval before production</span>' +
      '  </div>' +
      '  <div class="jrs-trust-band__row">' +
           _checkmarkSvg() +
      '    <span><strong>Unlimited revisions</strong> until the proof is exactly right</span>' +
      '  </div>' +
      '  <div class="jrs-trust-band__row">' +
           _checkmarkSvg() +
      '    <span><strong>No logo set-up fees</strong>. Your saved logo is free to reuse.</span>' +
      '  </div>' +
      '</div>'
    );
  }

  function _checkmarkSvg() {
    return '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M3 8 L7 12 L13 4"/></svg>';
  }

  // ---------------------------------------------------------------------
  // Logo grouping
  // ---------------------------------------------------------------------

  function _groupAssignmentsByLogo(positions, assignments) {
    var groups = [];
    var byLogoId = {};
    for (var i = 0; i < positions.length; i++) {
      var pid = positions[i];
      var assign = assignments[pid];
      if (!assign) continue;
      var key = (assign.source || 'unknown') + ':' + (assign.logoId || pid);
      if (byLogoId[key]) {
        byLogoId[key].positions.push(pid);
      } else {
        byLogoId[key] = {
          logoId: assign.logoId,
          source: assign.source,
          url: assign.url,
          filename: assign.filename,
          previewText: assign.previewText,
          positions: [pid]
        };
        groups.push(byLogoId[key]);
      }
    }
    return groups;
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions(editMode) {
    if (!_mountEl) return;
    var editButtons = _mountEl.querySelectorAll('[data-jrs-action="edit"]');
    for (var i = 0; i < editButtons.length; i++) {
      _bind(editButtons[i], 'click', _onEditClick);
    }
    // Phase per_product S3 — per-category "Edit" buttons (per-product review).
    var catEditButtons = _mountEl.querySelectorAll('[data-jrs-action="edit-category"]');
    for (var ce = 0; ce < catEditButtons.length; ce++) {
      _bind(catEditButtons[ce], 'click', _onEditCategoryClick);
    }
    // Phase 4 commit 6 — Cancel changes button is only rendered in
    // cart-edit mode. Bind only when present; the selector returns null
    // outside cart-edit and the bind is a no-op.
    if (editMode) {
      var cancelBtn = _mountEl.querySelector('[data-jrs-action="cart-edit-cancel"]');
      if (cancelBtn) _bind(cancelBtn, 'click', _onCartEditCancelClick);
    }

    _bindNudgeInteractions();
  }

  /**
   * Cancel changes button in the edit banner. Per Doc 6 §10.1 + Phase 4
   * plan §2-Q4: fire pdp_customizer_cart_edit_cancelled telemetry, then
   * close the popup via CartEditFlow.cancel() (which calls
   * JRS.CustomizerPopup.close with reason 'cart_edit_banner_cancel').
   *
   * No cart mutation. The popup was operating on a working copy in
   * state.draft; closing throws that away.
   */
  function _onCartEditCancelClick(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    // Telemetry FIRST so the close-triggered teardown can't drop it.
    // _emitCartEditCancelled reads state itself (had_pending_changes,
    // last_step_reached, ms_in_edit_session per Doc 3 §4.15).
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup._emitCartEditCancelled === 'function') {
      try { JRS.CustomizerPopup._emitCartEditCancelled(); }
      catch (telemetryErr) {
        if (window.console && typeof console.warn === 'function') {
          console.warn('jrs-v2-step-review: cart_edit_cancelled telemetry failed', telemetryErr);
        }
      }
    }

    // Close popup. Prefer CartEditFlow.cancel for the unified entry
    // point; fall back to a direct close if CartEditFlow is somehow
    // missing (shouldn't happen on cart pages — Tier 3 loads it).
    if (JRS.CartEditFlow && typeof JRS.CartEditFlow.cancel === 'function') {
      JRS.CartEditFlow.cancel();
    } else if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.close === 'function') {
      JRS.CustomizerPopup.close({ reason: 'cart_edit_banner_cancel', force: true });
    }
  }

  function _onEditClick(e) {
    var btn = e.currentTarget;
    if (!btn) return;
    var target = btn.getAttribute('data-jrs-edit-target');
    if (!target) return;

    // 2026-05-20 Bug 6 fix — when editing a logo group or position row,
    // carry the position-id so logo_picker (and downstream controllers
    // that read _current_position_for_logo) rehydrate the right
    // assignment. Without this, logo_picker would read whatever
    // _current_position_for_logo held from the END of the original
    // flow — usually the LAST position iterated, not the edited one.
    var positionId = btn.getAttribute('data-jrs-edit-position') || '';
    if (positionId) {
      var state = JRS.CustomizerPopupState.getState();
      var draft = state.draft || {};
      var positions = draft.positions || [];
      var cursorIdx = -1;
      for (var i = 0; i < positions.length; i++) {
        if (positions[i] === positionId) { cursorIdx = i; break; }
      }
      if (cursorIdx >= 0) {
        // Carry over the rest of draft; only override the two cursor
        // fields. Using SET_DRAFT (full replace) instead of two
        // SET_DRAFT_FIELDs to keep this atomic.
        var next = {};
        for (var k in draft) {
          if (Object.prototype.hasOwnProperty.call(draft, k)) next[k] = draft[k];
        }
        next._current_position_for_logo = positionId;
        next._position_cursor = cursorIdx;
        JRS.CustomizerPopupState.dispatch({
          type: 'SET_DRAFT',
          payload: { draft: next }
        });
      }
    }

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_review_edit', {
      step_id: STEP_ID,
      edit_target: target,
      edit_position: positionId || null
    });
    JRS.CustomizerPopup.goToStep(target, { trigger: 'review_edit' });
  }

  // Phase per_product S3 — per-category "Edit" click (per-product review).
  function _onEditCategoryClick(e) {
    var btn = e && e.currentTarget;
    if (!btn) return;
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    var categoryIndex = parseInt(btn.getAttribute('data-jrs-category-index'), 10);
    if (isNaN(categoryIndex)) return;
    handleEdit(categoryIndex, JRS.CustomizerPopupState.getState());
  }

  /**
   * Edit-from-review round-trip entry (Phase per_product S3, spec D9). Loads
   * category_configs[categoryIndex] back into the flat scratch draft, marks the
   * edit target, resets the logo-picker cursor (hydrate doesn't touch it), and
   * routes to position_picker. On the category's completion, flow-bundle's
   * completeCategoryOrAdvance sees _edit_category_index, re-snapshots, and
   * returns to review instead of advancing the forward cursor.
   *
   * @param {number} categoryIndex - REAL metaobject category index to edit
   * @param {Object} [state] - current popup state (re-read if omitted)
   */
  function handleEdit(categoryIndex, state) {
    var State = JRS.CustomizerPopupState;
    if (!State || typeof categoryIndex !== 'number' || categoryIndex < 0) return;

    // 1. Restore this category's branding into the flat scratch draft.
    if (typeof State.hydrateFlatDraftFromCategoryConfig === 'function') {
      State.hydrateFlatDraftFromCategoryConfig(categoryIndex);
    }

    // 2. Mark edit mode + point both cursors at this category. The logo-picker
    //    per-position cursor MUST be reset here: hydrate preserves the stale
    //    cursor from the prior flow, and (unlike forward flow) there's no
    //    resetFlatDraftForNextCategory call to zero it.
    _mergeDraftFields({
      _current_category_index: categoryIndex,
      _edit_category_index: categoryIndex,
      _position_cursor: 0,
      _current_position_for_logo: null
    });

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_review_edit', {
      step_id: STEP_ID,
      edit_target: 'position_picker',
      edit_category_index: categoryIndex
    });
    JRS.CustomizerPopup.goToStep('position_picker', { trigger: 'review_edit_category' });
  }

  // Atomic merge of fields onto the live draft (single SET_DRAFT dispatch).
  function _mergeDraftFields(fields) {
    var State = JRS.CustomizerPopupState;
    if (!State || typeof State.dispatch !== 'function') return;
    var draft = (typeof State.getState === 'function' && State.getState().draft) || {};
    var next = {};
    for (var k in draft) {
      if (Object.prototype.hasOwnProperty.call(draft, k)) next[k] = draft[k];
    }
    for (var f in fields) {
      if (Object.prototype.hasOwnProperty.call(fields, f)) next[f] = fields[f];
    }
    State.dispatch({ type: 'SET_DRAFT', payload: { draft: next } });
  }

  // Confirm-time guard for flat (multistep / bundle_wide) drafts: every
  // selected position needs a logo assignment before the cart build.
  //   - same_logo: copy the logo the other positions carry (the shopper
  //     chose one logo for all), silently.
  //   - otherwise: point the logo picker at the first position without a
  //     logo and send the shopper there.
  // Returns true when it routed away (Confirm must stop), false otherwise.
  // Per-product bundles keep their assignments in category_configs and are
  // checked per category by _repairMissingPerProductLogoAssignments.
  function _repairMissingLogoAssignments(state) {
    var draft = state.draft || {};
    if (draft.branding_strategy === 'per_product') return _repairMissingPerProductLogoAssignments(draft);
    var positions = draft.positions || [];
    var assignments = draft.logo_assignments || {};
    var missing = [];
    for (var i = 0; i < positions.length; i++) {
      if (!assignments[positions[i]]) missing.push(positions[i]);
    }
    if (missing.length === 0) return false;

    var source = null;
    if (draft.logo_strategy === 'same_logo') {
      for (var j = 0; j < positions.length; j++) {
        if (assignments[positions[j]]) { source = assignments[positions[j]]; break; }
      }
    }

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
      step_id: STEP_ID,
      field_id: 'logo_assignments[' + missing[0] + ']',
      reason: source ? 'logo_missing_at_confirm_repaired' : 'logo_missing_at_confirm',
      position_count: positions.length,
      missing_count: missing.length
    });

    if (source) {
      var repaired = {};
      for (var k in assignments) {
        if (Object.prototype.hasOwnProperty.call(assignments, k)) repaired[k] = assignments[k];
      }
      for (var m = 0; m < missing.length; m++) repaired[missing[m]] = source;
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'logo_assignments', value: repaired }
      });
      return false;
    }

    _mergeDraftFields({
      _position_cursor: positions.indexOf(missing[0]),
      _current_position_for_logo: missing[0]
    });
    JRS.CustomizerPopup.goToStep('logo_picker', { trigger: 'confirm_missing_logo' });
    return true;
  }

  // Per-product counterpart of the guard above (the build throws
  // 'per_product category N position "…" has no logo assignment' otherwise).
  // Walks category_configs in the builder's order:
  //   - same_logo category: copy the logo its other positions carry, silently.
  //   - otherwise: reopen that category as a review Edit round-trip
  //     (hydrate, _edit_category_index, as handleEdit does) with the logo
  //     picker on the first position without a logo. On Continue the picker
  //     hands off to FlowBundle.completeCategoryOrAdvance, which re-snapshots
  //     the category and returns to review.
  // Returns true when it routed away (Confirm must stop), false otherwise.
  function _repairMissingPerProductLogoAssignments(draft) {
    var State = JRS.CustomizerPopupState;
    var configs = draft.category_configs || {};
    var keys = Object.keys(configs).map(function (k) { return parseInt(k, 10); })
      .filter(function (k) { return !isNaN(k); })
      .sort(function (a, b) { return a - b; });

    var repairedConfigs = null;
    for (var ki = 0; ki < keys.length; ki++) {
      var cfg = configs[keys[ki]] || {};
      var positions = cfg.positions || [];
      var assignments = cfg.logo_assignments || {};
      var missing = [];
      for (var i = 0; i < positions.length; i++) {
        if (!assignments[positions[i]]) missing.push(positions[i]);
      }
      if (missing.length === 0) continue;

      var source = null;
      if (cfg.logo_strategy === 'same_logo') {
        for (var j = 0; j < positions.length; j++) {
          if (assignments[positions[j]]) { source = assignments[positions[j]]; break; }
        }
      }

      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
        step_id: STEP_ID,
        field_id: 'category_configs[' + keys[ki] + '].logo_assignments[' + missing[0] + ']',
        reason: source ? 'logo_missing_at_confirm_repaired' : 'logo_missing_at_confirm',
        position_count: positions.length,
        missing_count: missing.length,
        edit_category_index: keys[ki]
      });

      if (source) {
        var repaired = {};
        for (var k in assignments) {
          if (Object.prototype.hasOwnProperty.call(assignments, k)) repaired[k] = assignments[k];
        }
        for (var m = 0; m < missing.length; m++) repaired[missing[m]] = source;
        if (!repairedConfigs) {
          repairedConfigs = {};
          for (var ck in configs) {
            if (Object.prototype.hasOwnProperty.call(configs, ck)) repairedConfigs[ck] = configs[ck];
          }
        }
        var repairedCfg = {};
        for (var ff in cfg) {
          if (Object.prototype.hasOwnProperty.call(cfg, ff)) repairedCfg[ff] = cfg[ff];
        }
        repairedCfg.logo_assignments = repaired;
        repairedConfigs[keys[ki]] = repairedCfg;
        continue;
      }

      // Commit any same_logo repairs made to earlier categories before routing.
      if (repairedConfigs) {
        State.dispatch({ type: 'SET_DRAFT_FIELD', payload: { field: 'category_configs', value: repairedConfigs } });
      }
      if (typeof State.hydrateFlatDraftFromCategoryConfig === 'function') {
        State.hydrateFlatDraftFromCategoryConfig(keys[ki]);
      }
      _mergeDraftFields({
        _current_category_index: keys[ki],
        _edit_category_index: keys[ki],
        _position_cursor: positions.indexOf(missing[0]),
        _current_position_for_logo: missing[0]
      });
      JRS.CustomizerPopup.goToStep('logo_picker', { trigger: 'confirm_missing_logo' });
      return true;
    }

    if (repairedConfigs) {
      State.dispatch({ type: 'SET_DRAFT_FIELD', payload: { field: 'category_configs', value: repairedConfigs } });
    }
    return false;
  }

  function _onConfirmClick(e) {
    var state = JRS.CustomizerPopupState.getState();
    var draft = state.draft || {};
    var ctaBtn = e && e.currentTarget ? e.currentTarget : null;
    var isCartEdit = !!(state.editMode && state.editMode.active);

    // Phase 4 commit 6 — route to CartEditFlow.saveChanges when in
    // cart-edit mode. Save flow handles the atomic remove-then-add,
    // rollback semantics, and success/failure telemetry. The PDP path
    // remains JRS.CartSubmit.submitAndVerify (Phase 2 closeout).
    if (isCartEdit) {
      return _runCartEditSave(ctaBtn, state);
    }

    var attemptIndex = _nextAttemptIndex();

    if (!JRS.CartSubmit || typeof JRS.CartSubmit.submitAndVerify !== 'function') {
      _showSubmitError('Cart submission unavailable. Please refresh and try again.');
      _fireConfirmFailed({
        step_id: STEP_ID,
        flow: state.flow,
        error_reason: 'cart_submit_module_unavailable'
      }, attemptIndex, null);
      return;
    }

    // A position with no logo would make the build throw (logo_setup_missing).
    // Repair or re-ask before building, so the shopper never sees that error.
    if (_repairMissingLogoAssignments(state)) return;
    state = JRS.CustomizerPopupState.getState(); // re-read: a same_logo repair updates the draft
    draft = state.draft || {};

    _setSubmitting(ctaBtn, true);
    _clearSubmitError();

    var context = _resolvePageContext();
    var productConfig;
    try {
      productConfig = JRS.CartSubmit.buildProductConfiguration(state, context);
      // Doc 10 P1 — attach the parent provenance block (variant sources, flow,
      // session id, build, device). Set here (NOT in buildProductConfiguration) so
      // the byte-locked §2 fixtures stay unchanged; the builder stamps it as
      // _jrs_provenance on the parent line only. Best-effort — never blocks confirm.
      try {
        if (productConfig && typeof JRS.CartSubmit.buildProvenance === 'function') {
          productConfig.provenance = JRS.CartSubmit.buildProvenance(state, context);
        }
      } catch (pe) { /* provenance is diagnostic — never block the add */ }
    } catch (err) {
      _setSubmitting(ctaBtn, false);
      _showSubmitError(_friendlyBuildError(err));
      var buildFields = {
        step_id: STEP_ID,
        flow: state.flow,
        error_reason: 'build_failed',
        error_message: (err && err.message) || 'unknown'
      };
      var bf = _friendlyBranchFields(err);
      for (var bk in bf) { if (Object.prototype.hasOwnProperty.call(bf, bk)) buildFields[bk] = bf[bk]; }
      _fireConfirmFailed(buildFields, attemptIndex, null);
      return;
    }

    JRS.CartSubmit.submitAndVerify(productConfig).then(function (result) {
      if (result.ok) {
        // Committed — drop the loading overlay and clear THIS product's saved
        // progress: the standalone selection store (the customizer draft is
        // cleared by the terminal success-step close). cart:addSucceeded also
        // clears the store, but do it here too so it's deterministic on confirm.
        _setSubmitting(ctaBtn, false);
        if (JRS.PdpSelectionStore && typeof JRS.PdpSelectionStore.clear === 'function') {
          try { JRS.PdpSelectionStore.clear(); } catch (e) {}
        }
        _recordConfirmedConfig(productConfig); // v8.1.4 — for is_repeat_add on a later failure
        // Success — fire confirm telemetry per Doc 3 v0.2 §4.11 then close
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_confirm', {
          step_id: STEP_ID,
          flow: state.flow,
          position_count: _totalPositionCount(draft),
          logo_strategy: draft.logo_strategy || null,
          // Phase per_product S3 — segment ATC completion by branding strategy.
          branding_strategy: draft.branding_strategy || null,
          cart_submit_pattern: result.pattern || 'unknown',
          group_splits: result.groupSplits || 0,            // v8.2.0 — groups halved after an HTTP 400 (form-field ceiling)
          items_added: (result.addedItems || []).length,
          healed: result.healed || 0,
          attempt_index: attemptIndex
        });
        // Run 3 (2026-09-03, revert 2026-09-02) — the router fires
        // post_add_complete and, on the REDIRECT arm only, takes over: close as
        // a converted confirm, clear the draft + saved selection, navigate. On
        // the modal arm ('modal' return) we fall through UNCHANGED to the
        // original variantaug success-step receipt below.
        if (JRS.PostAdd && typeof JRS.PostAdd.complete === 'function' &&
            JRS.PostAdd.complete({
              path: state.flow === 'bundle' ? 'popup_bundle_confirm' : 'popup_multistep_confirm'
            }) === 'redirect') {
          JRS.CustomizerPopup.close({ reason: 'confirm', isConfirm: true, force: true });
          if (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.dispatch === 'function') {
            JRS.CustomizerPopupState.dispatch({ type: 'RESET' });
          }
          if (JRS.PdpSelectionStore && typeof JRS.PdpSelectionStore.clear === 'function') {
            try { JRS.PdpSelectionStore.clear(); } catch (eClr) {}
          }
          return;
        }
        // Success Step (FR2/FR3) — instead of closing the popup, capture a
        // frozen display snapshot now (before any RESET) and route to the
        // terminal 'success' step. If the success controller failed to load,
        // fall back to the prior close() so the confirm always completes.
        var successVariant = _resolveVariant(state, context);
        if (JRS.CustomizerSuccessStep &&
            typeof JRS.CustomizerSuccessStep.prepare === 'function' &&
            typeof JRS.CustomizerPopup.goToStep === 'function') {
          JRS.CustomizerSuccessStep.prepare(
            _buildSuccessSnapshot(state, draft, context, successVariant, result)
          );
          JRS.CustomizerPopup.goToStep('success', { trigger: 'confirm' });
        } else {
          JRS.CustomizerPopup.close({ reason: 'confirm', isConfirm: true, force: true });
        }
        return;
      }

      // Failed — partialSuccess or full failure
      _setSubmitting(ctaBtn, false);
      if (result.partialSuccess) {
        var partialStockView = _stockFailureView(result, productConfig, context); // v8.1.4
        if (_isRateLimited(result)) {
          _showSubmitError(RATE_LIMIT_MESSAGE);
          _startConfirmCooldown(ctaBtn, RATE_LIMIT_COOLDOWN_S);
        } else if (partialStockView) {
          _showSubmitError(partialStockView.message);
          if (partialStockView.holdCta) _holdConfirmAfterMismatch(ctaBtn, productConfig);
        } else {
          _showSubmitError(
            'Some items added but verification failed. Please review your cart before checkout.'
          );
        }
        var partialFields = {
          step_id: STEP_ID,
          flow: state.flow,
          error_reason: 'partial_success',
          items_added: (result.addedItems || []).length,
          failed_index: result.failedIndex,
          missing_count: (result.missing || []).length,
          is_repeat_add: _isRepeatAdd(productConfig),
          failure_class: (partialStockView && partialStockView.fields.failure_class) || (result.stockFailure && result.stockFailure.failureClass) || 'other'
        };
        if (partialStockView) { for (var pk in partialStockView.fields) { if (Object.prototype.hasOwnProperty.call(partialStockView.fields, pk)) partialFields[pk] = partialStockView.fields[pk]; } }
        if (partialStockView && partialStockView.fields.failure_class === 'partial_availability') {
          return _runBrandingCorrection(result, partialStockView, productConfig, context, state, partialFields).then(function () {
            _fireConfirmFailed(partialFields, attemptIndex, result);
          });
        }
        _fireConfirmFailed(partialFields, attemptIndex, result);
      } else if (result.unverifiable) {
        // v8.1.5b — the authoritative read was refused (429 challenge / network): we
        // could not check the cart. Say so; do not report a miss, do not invite a re-add.
        var UNVERIFIABLE_MESSAGE = 'Your items were sent to your cart, but Shopify paused our connection before we could confirm them — nothing you did wrong. ' +
          'Please wait a minute, then open your cart to check it before checkout. Do not add this again unless the cart is empty.';
        _showSubmitError(UNVERIFIABLE_MESSAGE);
        // Hold (configuration-fingerprint), not a 60 s cooldown: the lines may well be in the
        // cart, so Confirm must not come back on its own for the same configuration.
        _holdConfirmAfterMismatch(ctaBtn, productConfig);
        var udiag = result.diagnostics || {};
        _fireConfirmFailed({
          step_id: STEP_ID,
          flow: state.flow,
          error_reason: 'cart_verification_unverifiable',
          friendly_branch: result.bypassChallenge ? 'rate_limited' : 'unverifiable',
          missing_count: (result.missing || []).length,
          expected_line_count: udiag.expectedLineCount,
          matched_line_count: udiag.matchedLineCount,
          verify_reads: udiag.verifyReads,
          verify_watermark_stale: udiag.verifyWatermarkStale,
          verify_bypass_read: udiag.verifyBypassRead,
          verify_bypass_status: result.bypassStatus,
          shopify_challenge: !!result.bypassChallenge,
          group_results: _summariseGroupResults(result.groupResults)
        }, attemptIndex, result);
      } else if (result.missing && result.missing.length > 0) {
        // v8.1.2 — every POST returned 2xx, yet verification could not account for
        // some expected lines (live 2026-09-03 10:57: 3 of 12, one size group; the
        // "please try again" message then produced a full second copy — 30 duplicate
        // units). Whether the lines never landed or were removed after landing,
        // re-posting the whole configuration is the one action that is wrong in
        // both cases. So: if ANY line landed, treat the add as landed-with-mismatch —
        // no retry offer, clear the saved selection so a re-press cannot rebuild the
        // same add, stamp the mismatch for fulfilment, and send the shopper to the
        // cart. Only when NOTHING landed is a retry safe, and the copy says so.
        var diag = result.diagnostics || {};
        var matched = typeof diag.matchedLineCount === 'number' ? diag.matchedLineCount : ((result.addedItems || []).length - result.missing.length);
        var nothingLanded = matched <= 0;
        if (nothingLanded) {
          _showSubmitError(
            'We couldn’t confirm your items reached the cart, and nothing was added. Please press Confirm again — if it happens twice, contact us and we’ll sort it.'
          );
        } else {
          _showSubmitError(
            'Your items are in your cart, but ' + result.missing.length + ' of ' + (diag.expectedLineCount || ((result.addedItems || []).length)) +
            ' lines could not be confirmed. Please do NOT add this again — open your cart to check it. We’ve flagged this order for a manual check.'
          );
          _holdConfirmAfterMismatch(ctaBtn, productConfig);
          _stampVerifyMismatch(result, state);
          if (JRS.PdpSelectionStore && typeof JRS.PdpSelectionStore.clear === 'function') {
            try { JRS.PdpSelectionStore.clear(); } catch (eClr2) {}
          }
        }
        var vf = {
          step_id: STEP_ID,
          flow: state.flow,
          error_reason: 'cart_verification_failed',
          missing_count: result.missing.length,
          nothing_landed: nothingLanded,
          expected_line_count: diag.expectedLineCount,
          matched_line_count: diag.matchedLineCount,
          cart_line_count_after: diag.cartLineCountAfter,
          cart_unit_count_after: diag.cartUnitCountAfter,
          pre_heal_missing_count: diag.preHealMissingCount,
          heal_issues_found: diag.healIssuesFound, heal_skipped: diag.healSkipped === true,
          healed: diag.healed,
          verify_reread: diag.verifyReread,
          // v8.2.0 — did the reads prove themselves stale, and did the bypass read see the lines?
          verify_reads: diag.verifyReads,
          verify_watermark_stale: diag.verifyWatermarkStale,
          verify_updatedat: diag.verifyUpdatedAt,
          verify_last_post_at: diag.verifyLastPostAt,
          verify_bypass_read: diag.verifyBypassRead,
          verify_bypass_found: diag.verifyBypassFound,
          missing_lines: diag.missingLines,
          group_results: _summariseGroupResults(result.groupResults)
        };
        _fireConfirmFailed(vf, attemptIndex, result);
      } else if (_isRateLimited(result)) {
        // v8.1.1 — 429 / challenge: never route to the generic "review your
        // selections" fallback; hold the CTA so the re-press loop cannot start.
        _showSubmitError(RATE_LIMIT_MESSAGE);
        _startConfirmCooldown(ctaBtn, RATE_LIMIT_COOLDOWN_S);
        _fireConfirmFailed({
          step_id: STEP_ID,
          flow: state.flow,
          error_reason: 'submit_failed',
          error_message: (result && result.error) || 'HTTP 429',
          friendly_branch: 'rate_limited',
          raw_error: String((result && result.error) || 'HTTP 429').slice(0, 200),
          cooldown_s: RATE_LIMIT_COOLDOWN_S
        }, attemptIndex, result);
      } else {
        var stockView = _stockFailureView(result, productConfig, context); // v8.1.4
        var submitFields = {
          step_id: STEP_ID,
          flow: state.flow,
          error_reason: 'submit_failed',
          error_message: (result && result.error) || 'unknown',
          is_repeat_add: _isRepeatAdd(productConfig)
        };
        if (stockView) {
          _showSubmitError(stockView.message);
          if (stockView.holdCta) _holdConfirmAfterMismatch(ctaBtn, productConfig); // no retry — it cannot succeed
          submitFields.friendly_branch = 'stock_' + stockView.fields.failure_class;
          for (var stk in stockView.fields) { if (Object.prototype.hasOwnProperty.call(stockView.fields, stk)) submitFields[stk] = stockView.fields[stk]; }
          if (stockView.fields.failure_class === 'partial_availability') {
            // v8.1.4b — cap the branding lines to the garments that landed, then fire
            return _runBrandingCorrection(result, stockView, productConfig, context, state, submitFields).then(function () {
              _fireConfirmFailed(submitFields, attemptIndex, result);
            });
          }
        } else {
          _showSubmitError(_friendlyBuildError(result && result.error));
          submitFields.failure_class = 'other';
          var sf = _friendlyBranchFields(result && result.error);
          for (var sk in sf) { if (Object.prototype.hasOwnProperty.call(sf, sk)) submitFields[sk] = sf[sk]; }
        }
        _fireConfirmFailed(submitFields, attemptIndex, result);
      }
    }).catch(function (err) {
      _setSubmitting(ctaBtn, false);
      _showSubmitError(_friendlyBuildError(err));
      var threwFields = {
        step_id: STEP_ID,
        flow: state.flow,
        error_reason: 'submit_threw',
        error_message: (err && err.message) || 'unknown'
      };
      var tf = _friendlyBranchFields(err);
      for (var tk in tf) { if (Object.prototype.hasOwnProperty.call(tf, tk)) threwFields[tk] = tf[tk]; }
      _fireConfirmFailed(threwFields, attemptIndex, null);
    });
  }

  // ---------------------------------------------------------------------
  // Cart-edit save path (Phase 4 commit 6)
  // ---------------------------------------------------------------------

  /**
   * Run the cart-edit save flow. Dispatches to JRS.CartEditFlow.saveChanges
   * (commit 5 rewrite) and handles the CTA loading state + error surface.
   *
   * Success: popup closes inside CartEditFlow; the CTA never returns to
   * idle state (popup is gone). Telemetry is fired by CartEditFlow.
   *
   * Failure: surface the error inline, return CTA to idle state, leave
   * popup open for retry. CartEditFlow has already fired the failure
   * telemetry; we don't double-fire.
   */
  function _runCartEditSave(ctaBtn, state) {
    if (!JRS.CartEditFlow || typeof JRS.CartEditFlow.saveChanges !== 'function') {
      _showSubmitError('Cart-edit save unavailable. Please refresh and try again.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_cart_edit_failed', {
        step_id: STEP_ID,
        reason: 'cart_edit_flow_module_unavailable',
        phase: 'preflight'
      });
      return;
    }

    _setCtaLoading(ctaBtn, true);
    _clearSubmitError();

    JRS.CartEditFlow.saveChanges().then(function (result) {
      if (result && result.ok) {
        // Success — CartEditFlow already closed the popup; nothing more
        // to do here. CTA is gone with the popup so loading state is
        // moot. If for any reason the popup is still visible (close
        // failed), we leave the loading state on so the user can't
        // double-click.
        return;
      }

      // Diagnostics — the on-screen copy is humanised (often the generic
      // fallback), which hides the ROOT cause. Log the raw reason + error so a
      // failing cart-edit save is debuggable from the browser console. Not shown
      // to the customer.
      if (window.console && typeof console.error === 'function') {
        console.error('[JRS cart-edit] save failed — reason:', (result && result.reason),
          '| error:', (result && result.error), '| result:', result);
      }

      // Phase 4 commit 10 — critical failure path. The cart-edit-flow
      // rollback itself failed (Doc 9 §6.4), so the customer's cart
      // is in a known-bad state: original lines removed, new add
      // failed, AND the snapshot-based rollback also failed. The
      // only safe recovery is a full page reload so the customer
      // sees the actual cart state. Replace the entire footer with
      // a critical banner + Reload page CTA per Doc 9 §9.1.
      //
      // CartEditFlow already fired pdp_customizer_cart_edit_failed AND
      // pdp_customizer_cart_edit_rollback_failed telemetry before
      // returning the {critical:true} result, so we don't double-fire.
      if (result && result.critical === true) {
        _renderCriticalErrorState(result.error);
        return;
      }
      // Add-before-remove (2026-09-14): a 429 challenge stops all cart requests;
      // the edit resumes after a reload, so the recovery action is the same.
      if (result && result.reloadRequired === true) {
        _renderCriticalErrorState(result.error);
        return;
      }

      // Failure — restore CTA + surface inline error. CartEditFlow
      // already fired the appropriate cart_edit_failed telemetry.
      _setCtaLoading(ctaBtn, false);
      var fallbackMessage = 'Could not update cart. Please try again.';
      var message = (result && result.error) || fallbackMessage;

      // Doc 9 §9.1 error-copy mapping. Some reasons get a more specific
      // user-facing message than the raw error from the CartAPI layer.
      // `add_failed_rollback_failed` is also handled here as a fallback
      // in case the CartEditFlow contract ever drifts and stops setting
      // critical:true on that reason — but the primary path for that
      // reason is the _renderCriticalErrorState branch above.
      if (result && result.reason === 'add_failed_rollback_succeeded') {
        message = 'Couldn\'t apply your changes. Your cart is back to how it was before.';
      } else if (result && result.reason === 'add_failed_rollback_failed') {
        message = 'We couldn\'t update your cart and one item may be missing. ' +
                  'Please refresh the page to see your current cart.';
      } else if (result && result.reason === 'snapshot_failed') {
        message = 'Couldn\'t read your cart. Please refresh the page and try again.';
      } else if (result && result.reason === 'remove_failed') {
        message = 'Couldn\'t update the cart. Please try again.';
      } else if (result && result.reason === 'add_failed_no_mutation') {
        message = 'Couldn\'t apply your changes. Your cart hasn\'t changed — please try again.';
      } else if (result && result.reason === 'partial_add_reverted') {
        message = 'Couldn\'t apply all of your changes, so your cart is back to how it was. Please try again.';
      } else if (result && result.reason === 'build_failed') {
        message = _friendlyBuildError(result && result.error);
      }

      _showSubmitError(message);
    }).catch(function (err) {
      // CartEditFlow shouldn't throw — it converts everything to
      // {ok:false, ...} — but defend against unexpected rejection.
      _setCtaLoading(ctaBtn, false);
      _showSubmitError(
        (err && err.message) || 'Cart-edit save threw an unexpected error.'
      );
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_cart_edit_failed', {
        step_id: STEP_ID,
        reason: 'save_changes_threw',
        phase: 'unknown',
        error_message: (err && err.message) || 'unknown'
      });
    });
  }

  /**
   * Phase 4 commit 10 — Doc 9 §9.1 critical failure UX.
   *
   * Cart-edit save's rollback failed (Doc 9 §6.4). Customer's cart is
   * in a known-bad state. Replace the standard footer with a critical-
   * error banner + a Reload page button so the customer can recover by
   * fetching the actual cart state from Shopify.
   *
   * The Cancel button in the edit-banner (top of the review step) stays
   * visible — the customer can close the popup at their own risk
   * without reloading, but the prominent action is Reload page.
   *
   * Idempotent: calling twice doesn't double-render. The footer
   * replacement is keyed on the existence of [data-jrs-cart-edit-
   * critical-banner], so a second call no-ops.
   *
   * The translation keys customizer.cart_edit.critical_error_message
   * and customizer.cart_edit.critical_reload_cta are defined in
   * en.default.json (commit 9). The v2 stack doesn't yet have a JS-
   * side translate function — strings are inline English matching
   * the keys' values. Phase 5 polish: wire a window.JRS_LOCALE_STRINGS
   * bridge so JS-rendered copy honours locale.
   */
  function _renderCriticalErrorState(message) {
    if (!_mountEl) return;
    var body = _mountEl.querySelector('.jrs-step__body');
    if (!body) return;
    // Idempotency guard.
    if (body.querySelector('[data-jrs-cart-edit-critical-banner]')) return;

    _clearSubmitError();

    // Critical message banner at the top of the body (role="alert" so screen
    // readers announce it on injection — WAI-ARIA: alert is for time-sensitive
    // messages needing no dismissal). The recovery action (Reload page)
    // replaces the Confirm CTA in the sticky chrome footer.
    var banner = document.createElement('div');
    banner.className = 'jrs-cart-edit-critical-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('data-jrs-cart-edit-critical-banner', '');
    banner.innerHTML =
      '<p class="jrs-cart-edit-critical-banner__message">' +
      _escape(message || 'Your cart could not be updated. Please reload the page and try again.') +
      '</p>';
    if (body.firstChild) {
      body.insertBefore(banner, body.firstChild);
    } else {
      body.appendChild(banner);
    }
    try { banner.scrollIntoView({ block: 'nearest' }); } catch (_e) {}

    // Swap the chrome-footer Confirm CTA for a Reload-page recovery action.
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.setPrimaryCta === 'function') {
      JRS.CustomizerPopup.setPrimaryCta({
        label: 'Reload page',
        onClick: _onCriticalReloadClick
      });
    }
    // Auto-focus the reload CTA so keyboard users land on the recovery
    // action immediately. It lives in the shell chrome, so query the doc.
    try {
      var reloadBtn = document.querySelector('[data-jrs-popup-cta]');
      if (reloadBtn) reloadBtn.focus();
    } catch (_e2) {}
  }

  function _onCriticalReloadClick(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // No analytics fire here — the critical failure was already
    // telemetered by CartEditFlow.saveChanges (pdp_customizer_cart_
    // edit_failed with reason=add_failed_rollback_failed AND
    // pdp_customizer_cart_edit_rollback_failed). A "reload clicked"
    // event would be informative but isn't in Doc 3 v0.2 / v0.3.
    // Phase 5 polish: add pdp_customizer_cart_edit_reload_clicked if
    // we want to distinguish customers who saw the critical UI but
    // closed the popup vs reloaded.
    if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
      window.location.reload();
    }
  }

  // ---------------------------------------------------------------------
  // Submit-CTA UX helpers
  // ---------------------------------------------------------------------

  // Drive the shell's chrome-footer CTA for the review step: the
  // Confirm/Update button + the contextual microcopy as the reassurance
  // line. Always enabled (you only reach review with a complete config); the
  // submit flow toggles loading via _setCtaLoading → updatePrimaryCta.
  function _syncCta(editMode) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    var isCartEdit = !!editMode;
    var label = isCartEdit ? 'Update cart' : 'Add to cart';
    var microcopy = isCartEdit
      ? 'Saving replaces the existing item with your changes.'
      : 'Design proof emailed before production starts.';
    pop.setReassurance(microcopy);
    if (typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({ label: label, onClick: _onConfirmClick });
    }
    // v8.2.0 — a mismatch hold survives the re-mount unless the configuration changed
    try { if (!isCartEdit) _reapplyHoldIfSameConfig(JRS.CustomizerPopupState.getState()); } catch (e) {}
  }

  // Loading state now lives on the chrome-footer CTA (the Confirm button moved
  // there via setPrimaryCta). The ctaBtn param is retained for call-site
  // compatibility but ignored — the shell owns the slot button.
  function _setCtaLoading(ctaBtn, isLoading) {
    var pop = JRS.CustomizerPopup;
    if (pop && typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ loading: !!isLoading });
    }
  }

  // Full "submitting" state for the PDP confirm/add flow: the primary-CTA
  // spinner PLUS the full-sheet busy overlay ("Adding to cart…"), so a slow
  // bundle add (sequential POSTs) shows a clear, blocking loading animation.
  function _setSubmitting(ctaBtn, on) {
    _setCtaLoading(ctaBtn, on);
    var pop = JRS.CustomizerPopup;
    if (pop && typeof pop.setBusy === 'function') pop.setBusy(!!on);
  }

  // Map a raw build/submit error (thrown by the byte-locked builder / cart-submit
  // adapter — e.g. "Invalid configuration - _bundle_raw is 5775 chars…" or
  // "bundle children missing per-size data") to a short, actionable, customer-
  // facing sentence. The raw text still goes to telemetry (error_message) for
  // debugging; only the on-screen copy is humanised. Unknown errors fall back to
  // a friendly generic so a customer never sees a stack-trace-style message.
  function _friendlyBuildError(raw) {
    var m = String((raw && raw.message) || raw || '');
    // NOTE: the "_bundle_raw too large" branch was removed with Doc 2 §9.3 v0.7 —
    // the builder no longer blocks on _bundle_raw size (5,000 was never Shopify's
    // real ~1MB+ limit), so that message is unreachable and must never be shown.
    if (/per-size data|requires context\.bundle_children|no sizes|has no sizes/i.test(m)) {
      return 'Please choose a size for every product in your bundle before adding to cart.';
    }
    if (/positionVariantId|Cannot find variant|no usable variant/i.test(m)) {
      return 'One of your chosen positions isn’t available on this product right now. Please pick a different position, or get in touch and we’ll help.';
    }
    if (/missing catalogue descriptor|logo assignment|unknown logo source/i.test(m)) {
      return 'Something’s missing from your logo setup. Please review your selections and try again.';
    }
    return 'We couldn’t add this to your cart. Please review your selections and try again — if it keeps happening, contact us and we’ll sort it for you.';
  }

  // v8.1.1 — Shopify's request-velocity protection (HTTP 429, an interstitial
  // page a fetch() cannot clear). Retrying makes it worse: every press is
  // another burst against the same block. Say what happened, ask for a pause,
  // and point at the one action that clears it (a page load). The shopper's
  // selections survive a reload via the resume surface.
  var RATE_LIMIT_MESSAGE = 'Shopify paused your connection for a moment because several requests arrived at once — nothing you did wrong. ' +
    'Please wait a minute, then reload this page and press Confirm again. Your selections are saved.';
  var RATE_LIMIT_COOLDOWN_S = 60;
  var _cooldownTimer = null;

  // v8.1.2 — after a landed-with-mismatch verification, the Confirm CTA must not
  // rebuild and re-post the same configuration. Replace it with a cart link.
  // v8.2.0 — the hold must survive a re-mount of the review step. Re-entering
  // review via Edit re-runs _syncCta → setPrimaryCta, which re-created an enabled
  // Confirm (live 2026-09-04 08:05: a full duplicate posted that way). Remember
  // the configuration signature the hold was applied to; _syncCta re-applies the
  // hold when the current configuration still matches it, and releases it when
  // the shopper actually changed something (a legitimate new add).
  var _heldSignatures = {};
  function _currentSignature(state) {
    try {
      if (!JRS.CartSubmit || typeof JRS.CartSubmit.buildProductConfiguration !== 'function') return '';
      return _configSignature(JRS.CartSubmit.buildProductConfiguration(state, _resolvePageContext()));
    } catch (e) { return ''; }
  }
  function _reapplyHoldIfSameConfig(state) {
    var sig = _currentSignature(state); var k = _sessionKey();
    if (sig && _heldSignatures[k] && _heldSignatures[k] === sig) {
      _holdConfirmAfterMismatch(null, null);
      return true;
    }
    return false;
  }

  function _holdConfirmAfterMismatch(ctaBtn, productConfig) {
    var pop = JRS.CustomizerPopup;
    if (_cooldownTimer) { clearInterval(_cooldownTimer); _cooldownTimer = null; }
    if (productConfig) { try { var hs = _configSignature(productConfig); if (hs) _heldSignatures[_sessionKey()] = hs; } catch (e) {} }
    if (pop && typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ disabled: true, label: 'Added — check your cart' });
    }
    try {
      var err = _mountEl && _mountEl.querySelector('[data-jrs-review-error]');
      if (err && !err.querySelector('a[href="/cart"]')) {
        var a = document.createElement('a');
        a.href = '/cart'; a.textContent = ' Open cart'; a.setAttribute('data-jrs-open-cart', '');
        a.style.marginLeft = '6px'; a.style.textDecoration = 'underline';
        err.appendChild(a);
      }
    } catch (e) {}
  }

  // v8.1.2 — leave a fulfilment-visible witness on the cart (best effort).
  function _stampVerifyMismatch(result, state) {
    try {
      if (!JRS.CartAPI || typeof JRS.CartAPI.updateAttributes !== 'function') return;
      var d = result.diagnostics || {};
      var record = {
        // build stamp derived from the theme's own version stamp so it can never go stale
        v: String((window._bcThemeVersion || '')).replace(/^\d+-/, '') || 'JRSBASE-V8.3.0-09102026-BYOB',
        ts: new Date().toISOString(), flow: state.flow, pattern: result.pattern || '',
        expected: d.expectedLineCount, matched: d.matchedLineCount, missing: d.missingLines || '',
        sid: (JRS.CustomizerPopup && JRS.CustomizerPopup.getSessionId && JRS.CustomizerPopup.getSessionId()) || ''
      };
      JRS.CartAPI.updateAttributes({ '_jrs_verify_mismatch': JSON.stringify(record) }).catch(function () {});
    } catch (e) {}
  }

  // v8.2.0 — GA4 truncates parameter values at 100 characters. Encode compactly:
  // "<groups>g ok" when every group echoed exactly what was posted, otherwise
  // "<groups>g " + only the anomalous groups as idx:posted/responded:qty/qty,
  // capped at 100 characters with a trailing "…".
  function _summariseGroupResults(groupResults) {
    try {
      var list = groupResults || [];
      var anomalies = [];
      for (var i = 0; i < list.length; i++) {
        var g = list[i];
        if (g.posted !== g.responded || g.postedQty !== g.respondedQty) {
          anomalies.push(g.index + ':' + g.posted + '/' + g.responded + ':' + g.postedQty + '/' + g.respondedQty);
        }
      }
      var s = list.length + 'g ' + (anomalies.length ? anomalies.join(',') : 'ok');
      return s.length > 100 ? s.slice(0, 99) + '…' : s;
    } catch (e) { return ''; }
  }

  // ---------------------------------------------------------------------
  // v8.1.4 — stock failures named to the shopper (operator ruling 2026-09-04:
  // "this is in your cart already and that's the max stock we got … so they
  // know it's a stock issue and not something general"). Shopify's 422 text
  // is accurate; the theme used to discard it into the generic fallback.
  // ---------------------------------------------------------------------

  // Resolve the refused garment's title / size from the configuration that was
  // posted (the 422 body names nothing except on sold-out).
  function _describeVariant(productConfig, variantId, context) {
    var out = { productTitle: '', variantTitle: '', colour: '', size: '' };
    var cats = (productConfig && productConfig.categories) || [];
    for (var c = 0; c < cats.length; c++) {
      var prods = cats[c].products || [];
      for (var p = 0; p < prods.length; p++) {
        var sizes = prods[p].sizes || [];
        for (var s = 0; s < sizes.length; s++) {
          if (Number(sizes[s].variantId) === Number(variantId)) {
            out.productTitle = String(prods[p].productTitle || '');
            out.colour = String(sizes[s].colour || prods[p].colour || '');
            out.size = String(sizes[s].size || '');
            out.variantTitle = [out.colour, out.size].filter(Boolean).join(' / ');
            return out;
          }
        }
      }
    }
    if (!out.productTitle && context && context.productTitle) out.productTitle = String(context.productTitle);
    return out;
  }

  // The garment lines (no _ParentVariant) of a group, for "N of M" wording.
  function _groupGarmentQty(result) {
    var fg = result && result.failedGroup;
    return fg ? Number(fg.garmentRequestedQty || fg.requestedQty || 0) : 0;
  }

  // Returns null when the failure is not a stock class (caller keeps the generic path).
  function _stockFailureView(result, productConfig, context) {
    var sf = result && result.stockFailure;
    if (!sf || !sf.failureClass || sf.failureClass === 'other') return null;
    var fg = result.failedGroup || {};
    var variantId = Number(fg.garmentVariantId || fg.variantId || 0);
    var desc = _describeVariant(productConfig, variantId, context);
    var name = desc.productTitle || (context && context.productTitle) || 'this product';
    var label = name + (desc.variantTitle ? ' (' + desc.variantTitle + ')' : '');
    var requested = _groupGarmentQty(result);
    var message, holdCta = false;
    if (sf.failureClass === 'already_in_cart') {
      message = label + ' is already in your cart at the maximum we have in stock' +
        (requested ? ' (you asked for ' + requested + ' more)' : '') +
        '. Nothing new was added — your items are there. Open your cart to review them.';
      holdCta = true;
    } else if (sf.failureClass === 'partial_availability') {
      var accepted = Number(sf.acceptedQty || 0);
      // v8.1.4b — the branding lines are corrected automatically right after this
      // message is built (see _runBrandingCorrection); this is the interim copy and
      // the copy used if the correction could not be made.
      message = 'Only ' + accepted + ' of the ' + (requested || '?') + ' ' + label + ' you asked for could be added — that’s all the stock we have. ' +
        'The other ' + (requested ? Math.max(0, requested - accepted) : '?') + ' are not in your cart. ' +
        'Please open your cart before checkout: the branding lines were added for the full ' + (requested || '') + ', so the extra branding lines need removing.';
      holdCta = true;
    } else if (sf.failureClass === 'sold_out') {
      var soldName = sf.namedProduct ? sf.namedProduct + (sf.namedVariantTitle ? ' (' + sf.namedVariantTitle + ')' : '') : label;
      message = soldName + ' is sold out, so nothing was added. Please choose a different size or a smaller quantity and press Confirm again.';
    }
    return {
      message: message,
      holdCta: holdCta,
      fields: {
        failure_class: sf.failureClass,
        failed_variant_id: variantId,
        failed_product_title: desc.productTitle || sf.namedProduct || '',
        failed_variant_title: desc.variantTitle || sf.namedVariantTitle || '',
        requested_qty: requested,
        accepted_qty: (typeof sf.acceptedQty === 'number') ? sf.acceptedQty : null
      }
    };
  }

  // v8.1.4b — bring the branding lines of a partially-added group down to the
  // garments that landed, then rewrite the message to say so. Runs only on
  // partial_availability; never blocks; a failed correction keeps the manual copy,
  // stamps _jrs_branding_overage for fulfilment, and is reported in telemetry.
  function _runBrandingCorrection(result, stockView, productConfig, context, state, telemetryFields) {
    if (!stockView || stockView.fields.failure_class !== 'partial_availability') return Promise.resolve(null);
    if (!JRS.CartSubmit || typeof JRS.CartSubmit.correctBrandingAfterPartial !== 'function') return Promise.resolve(null);
    return JRS.CartSubmit.correctBrandingAfterPartial(result).then(function (fix) {
      var requested = stockView.fields.requested_qty || 0;
      var label = (stockView.fields.failed_product_title || 'this product') +
        (stockView.fields.failed_variant_title ? ' (' + stockView.fields.failed_variant_title + ')' : '');
      telemetryFields.branding_corrected = !!(fix && fix.ok);
      telemetryFields.branding_lines_corrected = (fix && fix.corrected) ? fix.corrected.length : 0;
      telemetryFields.branding_landed_qty = fix ? fix.landedQty : 0;
      if (fix && (fix.error || (fix.failed && fix.failed.length))) {
        telemetryFields.branding_correction_error = fix.error || ((fix.failed[0] && fix.failed[0].error) || 'change_failed');
      }
      if (fix && fix.ok) {
        var landed = fix.landedQty || stockView.fields.accepted_qty || 0;
        _showSubmitError(
          'Only ' + landed + ' of the ' + (requested || '?') + ' ' + label + ' you asked for could be added — that’s all the stock we have. ' +
          'We’ve matched your branding to those ' + landed + ', so you’ll only be charged for what’s in your cart. ' +
          'The other ' + (requested ? Math.max(0, requested - landed) : '?') + ' are not in your cart. Open your cart to review it.'
        );
      } else {
        // correction failed — keep the manual copy, leave a witness for fulfilment
        try {
          if (JRS.CartAPI && typeof JRS.CartAPI.updateAttributes === 'function') {
            JRS.CartAPI.updateAttributes({ '_jrs_branding_overage': JSON.stringify({
              v: String((window._bcThemeVersion || '')).replace(/^\d+-/, '') || 'JRSBASE-V8.3.0-09102026-BYOB', ts: new Date().toISOString(),
              garment_variant: (result.failedGroup && result.failedGroup.garmentVariantId) || 0,
              requested: requested, landed: fix ? fix.landedQty : 0, error: telemetryFields.branding_correction_error || 'unknown',
              sid: _sessionKey()
            }) }).catch(function () {});
          }
        } catch (e) {}
      }
      return fix;
    }).catch(function () { telemetryFields.branding_corrected = false; telemetryFields.branding_correction_error = 'threw'; return null; });
  }

  // v8.1.4 — is_repeat_add: has THIS popup session already confirmed the same
  // configuration? Signature = sorted garment variant:qty pairs + position variants.
  var _confirmedSignatures = {};
  function _configSignature(productConfig) {
    try {
      var parts = [];
      var cats = (productConfig && productConfig.categories) || [];
      for (var c = 0; c < cats.length; c++) {
        var prods = cats[c].products || [];
        for (var p = 0; p < prods.length; p++) {
          var sizes = prods[p].sizes || [];
          for (var s = 0; s < sizes.length; s++) parts.push('g' + sizes[s].variantId + ':' + sizes[s].quantity);
        }
      }
      var pos = (productConfig && productConfig.customization && productConfig.customization.positions) || [];
      for (var i = 0; i < pos.length; i++) parts.push('p' + pos[i].positionVariantId);
      return parts.sort().join('|');
    } catch (e) { return ''; }
  }
  function _sessionKey() {
    try { return String((JRS.CustomizerPopup && JRS.CustomizerPopup.getSessionId && JRS.CustomizerPopup.getSessionId()) || ''); } catch (e) { return ''; }
  }
  function _recordConfirmedConfig(productConfig) {
    var sig = _configSignature(productConfig); if (!sig) return;
    var k = _sessionKey(); _confirmedSignatures[k] = _confirmedSignatures[k] || {};
    _confirmedSignatures[k][sig] = true;
  }
  function _isRepeatAdd(productConfig) {
    var sig = _configSignature(productConfig); var k = _sessionKey();
    return !!(sig && _confirmedSignatures[k] && _confirmedSignatures[k][sig]);
  }

  function _isRateLimited(result) {
    return !!(result && (result.challenge === true || result.httpStatus === 429));
  }

  // Hold the Confirm CTA for a visible countdown after a 429, then re-enable it.
  // Never a permanent block: the button always comes back.
  function _startConfirmCooldown(ctaBtn, seconds) {
    var pop = JRS.CustomizerPopup;
    if (!pop || typeof pop.updatePrimaryCta !== 'function') return;
    if (_cooldownTimer) { clearInterval(_cooldownTimer); _cooldownTimer = null; }
    var remaining = seconds;
    var originalLabel = '';
    try {
      var l = ctaBtn && ctaBtn.querySelector ? ctaBtn.querySelector('[data-jrs-popup-cta-label]') : null;
      originalLabel = l ? l.textContent : '';
    } catch (e) {}
    var tick = function () {
      if (remaining <= 0) {
        clearInterval(_cooldownTimer); _cooldownTimer = null;
        pop.updatePrimaryCta({ disabled: false, label: originalLabel || 'Confirm' });
        return;
      }
      pop.updatePrimaryCta({ disabled: true, label: 'Please wait ' + remaining + 's…' });
      remaining -= 1;
    };
    tick();
    _cooldownTimer = setInterval(tick, 1000);
  }

  function _showSubmitError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-review-error]');
    if (err) {
      err.textContent = msg;
      err.hidden = false;
      // The error now sits at the end of the scrolling body (the CTA moved to
      // the chrome footer), so bring it into view on a submit failure.
      try { err.scrollIntoView({ block: 'nearest' }); } catch (_e) {}
    }
  }

  function _clearSubmitError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-review-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
  }

  // ---------------------------------------------------------------------
  // Login nudge (Doc 4 §7 location 3 — guest-only, dismissible review-step
  // sign-in prompt. Cross-step dismissal state lives on window.JRS so
  // dismissing in logo_picker also suppresses the review nudge for the
  // remainder of the page session.)
  // ---------------------------------------------------------------------

  function _renderLoginNudge(placement, message, opts) {
    // Guest login nudges disabled — merchant decision 2026-07-08.
    return '';
  }

  function _bindNudgeInteractions() {
    if (!_mountEl) return;
    var ctas = _mountEl.querySelectorAll('[data-jrs-action="login-nudge-cta"]');
    for (var i = 0; i < ctas.length; i++) _bind(ctas[i], 'click', _onNudgeCtaClick);
    var dismissBtns = _mountEl.querySelectorAll('[data-jrs-action="login-nudge-dismiss"]');
    for (var j = 0; j < dismissBtns.length; j++) _bind(dismissBtns[j], 'click', _onNudgeDismissClick);
  }

  function _onNudgeCtaClick(e) {
    var placement = e.currentTarget && e.currentTarget.getAttribute('data-placement');
    if (placement && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_clicked', {
        step_id: STEP_ID,
        placement: placement
      });
    }
    // Default anchor navigation proceeds.
  }

  function _onNudgeDismissClick(e) {
    var btn = e.currentTarget;
    var placement = btn && btn.getAttribute('data-placement');
    var nudgeEl = btn && (btn.closest ? btn.closest('[data-jrs-login-nudge]') : null);
    if (nudgeEl && nudgeEl.parentNode) nudgeEl.parentNode.removeChild(nudgeEl);
    if (placement) _markNudgeDismissed(placement);
    if (placement && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_dismissed', {
        step_id: STEP_ID,
        placement: placement
      });
    }
  }

  function _firePostRenderShownPass() {
    if (!_mountEl) return;
    var nudges = _mountEl.querySelectorAll('[data-jrs-login-nudge]');
    for (var i = 0; i < nudges.length; i++) {
      if (nudges[i].classList.contains('jrs-login-nudge--hidden')) continue;
      var placement = nudges[i].getAttribute('data-placement');
      if (placement) _maybeFireNudgeShown(placement);
    }
  }

  function _maybeFireNudgeShown(placement) {
    if (!placement || _shownNudgesThisMount[placement]) return;
    _shownNudgesThisMount[placement] = true;
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_login_nudge_shown', {
        step_id: STEP_ID,
        placement: placement
      });
    }
  }

  function _isNudgeDismissed(placement) {
    return !!(window.JRS && window.JRS._dismissedNudges && window.JRS._dismissedNudges[placement]);
  }

  function _markNudgeDismissed(placement) {
    window.JRS = window.JRS || {};
    window.JRS._dismissedNudges = window.JRS._dismissedNudges || {};
    window.JRS._dismissedNudges[placement] = true;
  }

  function _currentReturnUrl() {
    try {
      var loc = window.location || {};
      return (loc.pathname || '/') + (loc.search || '') + (loc.hash || '');
    } catch (_e) {
      return '/';
    }
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _methodLabel(method, perProduct) {
    // Display labels only — the underlying application_method VALUES
    // ('embroidery' / 'print') are unchanged. Per-product cards use the
    // past-tense pill form; bundle_wide / multistep keep the noun form.
    if (perProduct) {
      if (method === 'embroidery') return 'Embroidered';
      if (method === 'print') return 'Printed';
      return method || '—';
    }
    if (method === 'embroidery') return 'Embroidery';
    if (method === 'print') return 'Print';
    return method || '—';
  }

  function _sumSizes(sizes) {
    var total = 0;
    for (var i = 0; i < sizes.length; i++) total += (sizes[i].quantity || 0);
    return total;
  }

  function _formatMoney(cents, currencyCode) {
    var n = (cents || 0) / 100;
    var symbol = '£';
    if (currencyCode === 'USD') symbol = '$';
    else if (currencyCode === 'EUR') symbol = '€';
    if (n === Math.floor(n)) return symbol + n + '.00';
    return symbol + n.toFixed(2);
  }

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.Review = {
    // v8.2.0 test seams — group_results encoding + configuration fingerprint
    _summariseGroupResults: _summariseGroupResults,
    _configSignature: _configSignature,
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    // Edit-from-review entry (Phase per_product S3, spec D9). Day-to-day this
    // is driven by the per-category Edit button click; exposed for tests and
    // for any caller that needs to trigger a category edit programmatically.
    handleEdit: handleEdit,
    // Test seams — NOT part of the day-to-day public API. _resolvePageContext
    // (step-review-context.test.js) verifies bundle parent-id propagation;
    // _renderPerProductSections / _isPerProductReview (per-product-review.test.js)
    // verify the S3 per-category section markup + gating. Underscore prefix
    // matches the pattern JRS.CartSubmit uses for its test seams.
    _resolvePageContext: _resolvePageContext,
    _renderPerProductSections: _renderPerProductSections,
    _isPerProductReview: _isPerProductReview,
    _totalPositionCount: _totalPositionCount,
    // Per-category size + colour disclosure (per-product review) test seams.
    _categoryChildren: _categoryChildren,
    _groupChildSizesByColour: _groupChildSizesByColour,
    _renderCategorySizeDisclosure: _renderCategorySizeDisclosure,
    _renderBundleSummary: _renderBundleSummary,
    // Stage 2B — inline image fallbacks. Exposed because the per-product
    // photo (_garmentSvg) and logo-thumb (_logoBoxSvg) <img> onerror handlers
    // swap a failed image for these via window.JRS.Step.Review.
    _logoBoxSvg: _logoBoxSvg,
    _garmentSvg: _garmentSvg,
    // Success-step Stage 3 — the frozen display snapshot the terminal success
    // step renders from (success-snapshot.test.js verifies display labels,
    // file/text logo entries, qty>1 meta, missing-image flag).
    _buildSuccessSnapshot: _buildSuccessSnapshot,
    _snapshotLogo: _snapshotLogo,
    _buildBundleSnapshot: _buildBundleSnapshot,
    _sumChildSizes: _sumChildSizes,
    // Humanised build/submit error copy (raw technical throw → customer sentence).
    _friendlyBuildError: _friendlyBuildError,
    // Position display name (catalogue name, else humanised id — never a raw handle).
    _prettyPositionName: _prettyPositionName,
    _humanizePositionId: _humanizePositionId
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
